import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FaBookOpen,
    FaBullhorn,
    FaDownload,
    FaFile,
    FaFileAlt,
    FaFileExcel,
    FaFilePdf,
    FaFilePowerpoint,
    FaFileWord,
    FaGraduationCap,
    FaLightbulb,
    FaLock,
    FaQuestionCircle,
    FaStar,
    FaTools,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../api/api";
import QuestionAndAnswer from "./QuestionAndAnswer";
import StudentsNote from "./StudentsNote";

const CLASSROOM_STYLES = `
.cayep-classroom-environment {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 16px;
    overflow: hidden;
    color: #142536;
}

.cayep-classroom-empty {
    width: 100%;
    min-height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 16px;
    color: #142536;
}

.cayep-classroom-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 26px;
    margin-bottom: 16px;
}

.cayep-classroom-empty h3 {
    margin: 0 0 8px;
    color: #062A4B;
    font-size: 20px;
    font-weight: 800;
}

.cayep-classroom-empty p {
    margin: 0;
    color: #6B7280;
    font-size: 13px;
}

.cayep-classroom-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 22px;
    background: linear-gradient(
        135deg,
        #062A4B 0%,
        #063A6B 100%
    );
    color: #FFFFFF;
}

.cayep-classroom-header-main {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
}

.cayep-classroom-header-icon {
    width: 52px;
    height: 52px;
    min-width: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(246, 201, 68, 0.15);
    border: 1px solid rgba(246, 201, 68, 0.3);
    color: #F6C944;
    font-size: 22px;
}

.cayep-classroom-header-copy {
    min-width: 0;
}

.cayep-classroom-eyebrow {
    display: block;
    margin-bottom: 5px;
    color: #F6C944;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.2px;
}

.cayep-classroom-header-copy h2 {
    margin: 0;
    color: #FFFFFF;
    font-size: 21px;
    font-weight: 800;
    line-height: 1.25;
}

.cayep-classroom-video-context {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 12px;
}

.cayep-classroom-video-context svg {
    color: #F6C944;
}

.cayep-classroom-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 8px 11px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    font-size: 11px;
    font-weight: 700;
}

.cayep-classroom-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4FAE22;
    box-shadow: 0 0 0 3px rgba(79, 174, 34, 0.15);
}

.cayep-classroom-approval-notice {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    margin: 18px;
    padding: 15px;
    background: #FFF9E8;
    border: 1px solid #F1D985;
    border-radius: 11px;
}

.cayep-classroom-approval-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #FFF1C2;
    color: #C0750D;
}

.cayep-classroom-approval-notice strong {
    display: block;
    margin-bottom: 4px;
    color: #6B4B00;
    font-size: 13px;
    font-weight: 800;
}

.cayep-classroom-approval-notice p {
    margin: 0;
    color: #806A32;
    font-size: 12px;
    line-height: 1.5;
}

.cayep-classroom-tabs {
    display: flex;
    align-items: stretch;
    gap: 2px;
    padding: 8px;
    background: #F7F9FB;
    border-bottom: 1px solid #E0E7EC;
    overflow-x: auto;
}

.cayep-classroom-tabs button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 40px;
    padding: 8px 13px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #687684;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    transition: all 0.2s ease;
}

.cayep-classroom-tabs button:hover {
    background: #EEF3F7;
    color: #063A6B;
}

.cayep-classroom-tabs button.active {
    background: #FFFFFF;
    color: #062A4B;
    box-shadow: 0 2px 8px rgba(6, 42, 75, 0.08);
}

.cayep-classroom-tab-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}

.cayep-classroom-tabs button.active
.cayep-classroom-tab-icon {
    color: #C0750D;
}

.cayep-classroom-tab-count {
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 9px;
    font-weight: 800;
}

.cayep-classroom-panel {
    min-height: 400px;
    background: #FFFFFF;
}

.cayep-classroom-loading {
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #7A8793;
    font-size: 12px;
}

.cayep-classroom-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #E5ECF1;
    border-top-color: #0F6DAB;
    border-radius: 50%;
    animation: cayep-classroom-spin 0.8s linear infinite;
}

@keyframes cayep-classroom-spin {
    to {
        transform: rotate(360deg);
    }
}

.cayep-classroom-content-section {
    padding: 25px;
}

.cayep-classroom-section-heading {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 22px;
}

.cayep-classroom-heading-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 17px;
}

.cayep-classroom-section-kicker {
    display: block;
    margin-bottom: 5px;
    color: #0F6DAB;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1px;
}

.cayep-classroom-section-heading h3 {
    margin: 0 0 5px;
    color: #062A4B;
    font-size: 20px;
    font-weight: 800;
}

.cayep-classroom-section-heading p {
    margin: 0;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.5;
}

.cayep-classroom-context-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 10px;
    padding: 6px 9px;
    border-radius: 7px;
    background: #F4F7F9;
    border: 1px solid #E0E7EC;
    color: #526273;
    font-size: 10px;
    font-weight: 700;
}

.cayep-classroom-context-pill svg {
    color: #C0750D;
}

.cayep-classroom-rich-content {
    padding: 18px;
    background: #F7F9FB;
    border: 1px solid #E0E7EC;
    border-radius: 11px;
}

.cayep-classroom-rich-content p {
    margin: 0 0 12px;
    color: #374757;
    font-size: 13px;
    line-height: 1.75;
}

.cayep-classroom-rich-content p:last-child {
    margin-bottom: 0;
}

.cayep-classroom-empty-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 30px;
    text-align: center;
    background: #F7F9FB;
    border: 1px dashed #D5DEE5;
    border-radius: 12px;
}

.cayep-classroom-empty-panel > svg {
    margin-bottom: 12px;
    color: #0F6DAB;
    font-size: 27px;
}

.cayep-classroom-empty-panel h4 {
    margin: 0 0 6px;
    color: #062A4B;
    font-size: 15px;
    font-weight: 800;
}

.cayep-classroom-empty-panel p {
    max-width: 500px;
    margin: 0;
    color: #6B7280;
    font-size: 12px;
    line-height: 1.6;
}

.cayep-classroom-announcement-list,
.cayep-classroom-review-list,
.cayep-classroom-resource-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.cayep-classroom-announcement-card,
.cayep-classroom-review-card,
.cayep-classroom-resource-card {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 15px;
    background: #FFFFFF;
    border: 1px solid #E0E7EC;
    border-radius: 11px;
}

.cayep-classroom-announcement-icon,
.cayep-classroom-review-icon,
.cayep-classroom-resource-icon {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #EEF5FA;
    color: #0F6DAB;
}

.cayep-classroom-announcement-body,
.cayep-classroom-resource-info {
    flex: 1;
    min-width: 0;
}

.cayep-classroom-announcement-meta {
    margin-bottom: 4px;
    color: #7A8793;
    font-size: 10px;
}

.cayep-classroom-announcement-body h4 {
    margin: 0 0 5px;
    color: #062A4B;
    font-size: 14px;
    font-weight: 800;
}

.cayep-classroom-announcement-body p {
    margin: 0;
    color: #596978;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
}

.cayep-classroom-review-header {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cayep-classroom-review-header strong {
    display: block;
    color: #062A4B;
    font-size: 12px;
}

.cayep-classroom-review-header span {
    display: block;
    margin-top: 2px;
    color: #7A8793;
    font-size: 10px;
}

.cayep-classroom-review-card > p {
    flex: 1;
    margin: 0;
    color: #596978;
    font-size: 12px;
    line-height: 1.65;
    white-space: pre-wrap;
}

.cayep-classroom-resource-top {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cayep-classroom-resource-top h4 {
    margin: 0;
    color: #062A4B;
    font-size: 13px;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cayep-classroom-resource-extension {
    padding: 3px 6px;
    border-radius: 5px;
    background: #EEF5FA;
    color: #0F6DAB;
    font-size: 8px;
    font-weight: 800;
    flex-shrink: 0;
}

.cayep-classroom-resource-filename {
    margin: 3px 0 0;
    color: #7A8793;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cayep-classroom-resource-description {
    margin: 5px 0 0;
    color: #596978;
    font-size: 11px;
    line-height: 1.5;
}

.cayep-classroom-resource-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 7px;
    color: #7A8793;
    font-size: 9px;
}

.cayep-resource-meta-dot {
    color: #C4C7C7;
}

.cayep-classroom-resource-download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    border: 0;
    border-radius: 7px;
    padding: 8px 10px;
    background: #EEF3F7;
    color: #063A6B;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
}

.cayep-classroom-resource-download:hover {
    background: #DDEAF2;
}

.cayep-classroom-resource-download:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 760px) {
    .cayep-classroom-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-classroom-status {
        align-self: flex-start;
    }

    .cayep-classroom-tabs {
        scrollbar-width: thin;
    }

    .cayep-classroom-content-section {
        padding: 18px;
    }

    .cayep-classroom-resource-card {
        flex-wrap: wrap;
    }

    .cayep-classroom-resource-download {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .cayep-classroom-header {
        padding: 17px;
    }

    .cayep-classroom-header-icon {
        width: 44px;
        height: 44px;
        min-width: 44px;
    }

    .cayep-classroom-header-copy h2 {
        font-size: 18px;
    }

    .cayep-classroom-content-section {
        padding: 14px;
    }

    .cayep-classroom-section-heading {
        gap: 10px;
    }

    .cayep-classroom-heading-icon {
        width: 36px;
        height: 36px;
        min-width: 36px;
    }

    .cayep-classroom-section-heading h3 {
        font-size: 17px;
    }

    .cayep-classroom-announcement-card,
    .cayep-classroom-review-card {
        padding: 12px;
    }
}
`;

const EMPTY_CLASSROOM = {
    overview: null,
    questions: [],
    notes: null,
    announcements: [],
    reviews: [],
    resources: [],
};

const MANAGEMENT_ROLES = new Set([
    "teacher",
    "facilitator",
    "instructor",
    "admin",
    "administrator",
    "principal",
    "proprietor",
]);

const ClassRoomEnvironment = ({
    course,
    videoType = "introduction",
    videoSection = null,
}) => {
    const [activeTab, setActiveTab] =
        useState("overview");

    const [classroom, setClassroom] =
        useState(EMPTY_CLASSROOM);

    const [loading, setLoading] =
        useState(true);

    const [
        downloadingResourceId,
        setDownloadingResourceId,
    ] = useState(null);

    const [role, setRole] =
        useState("");

    const mountedRef =
        useRef(true);

    const classroomRequestRef =
        useRef(0);

    const courseId =
        course?.id;

    const isStudent =
        role === "student";

    const isManagement =
        MANAGEMENT_ROLES.has(role);

    const isSectionVideo =
        videoType === "section" &&
        Boolean(
            videoSection?.id ||
                videoSection?.section_id ||
                videoSection?.sectionId
        );

    const sectionId = useMemo(() => {
        if (!isSectionVideo) {
            return null;
        }

        return (
            videoSection?.id ||
            videoSection?.section_id ||
            videoSection?.sectionId ||
            null
        );
    }, [
        isSectionVideo,
        videoSection,
    ]);

    const learningStatus =
        useMemo(() => {
            const value =
                course?.learning_status ??
                course?.learningStatus ??
                course?.approval_status ??
                course?.approvalStatus ??
                "";

            return String(value)
                .trim()
                .toLowerCase();
        }, [course]);

    const hasExplicitLearningStatus =
        Boolean(learningStatus);

    const isApproved =
        !hasExplicitLearningStatus ||
        learningStatus === "approved" ||
        learningStatus === "active" ||
        learningStatus === "published";

    const isPending =
        Boolean(courseId) &&
        hasExplicitLearningStatus &&
        !isApproved;

    const videoContextLabel =
        useMemo(() => {
            if (!isSectionVideo) {
                return "Introduction Video";
            }

            const number =
                videoSection?.section_number ||
                videoSection?.sectionNumber ||
                "";

            const title =
                videoSection?.title ||
                videoSection?.name ||
                "Course Section";

            if (number) {
                return `Section ${number} • ${title}`;
            }

            return title;
        }, [
            isSectionVideo,
            videoSection,
        ]);

    const classroomPath =
        useMemo(() => {
            if (!courseId) {
                return "";
            }

            if (
                isSectionVideo &&
                sectionId
            ) {
                return `api/users/subjects/${courseId}/video-sections/${sectionId}/classroom/`;
            }

            return `api/users/subjects/${courseId}/classroom/`;
        }, [
            courseId,
            isSectionVideo,
            sectionId,
        ]);

    const getArray =
        useCallback(
            (value) => {
                if (
                    Array.isArray(value)
                ) {
                    return value;
                }

                if (
                    Array.isArray(
                        value?.results
                    )
                ) {
                    return value.results;
                }

                if (
                    Array.isArray(
                        value?.data
                    )
                ) {
                    return value.data;
                }

                return [];
            },
            []
        );

    const normalizeClassroomResponse =
        useCallback(
            (data) => {
                const source =
                    data?.data &&
                    typeof data.data ===
                        "object" &&
                    !Array.isArray(
                        data.data
                    )
                        ? data.data
                        : data || {};

                return {
                    overview:
                        source?.overview ||
                        source?.course_overview ||
                        source?.video_section_overview ||
                        null,

                    questions:
                        getArray(
                            source?.questions ||
                                source?.qa ||
                                source?.question_and_answers
                        ),

                    notes:
                        source?.notes ||
                        source?.note ||
                        null,

                    announcements:
                        getArray(
                            source?.announcements
                        ),

                    reviews:
                        getArray(
                            source?.reviews
                        ),

                    resources:
                        getArray(
                            source?.resources ||
                                source?.learning_resources ||
                                source?.course_resources
                        ),
                };
            },
            [getArray]
        );

    const readStoredRole =
        useCallback(() => {
            try {
                const storedUser =
                    localStorage.getItem(
                        "user"
                    );

                if (storedUser) {
                    const parsedUser =
                        JSON.parse(
                            storedUser
                        );

                    const storedRole =
                        parsedUser?.user_type ||
                        parsedUser?.role ||
                        parsedUser?.user_role ||
                        "";

                    if (
                        storedRole
                    ) {
                        return String(
                            storedRole
                        )
                            .trim()
                            .toLowerCase();
                    }
                }
            } catch (error) {
                console.warn(
                    "Unable to read stored user:",
                    error
                );
            }

            return String(
                localStorage.getItem(
                    "role"
                ) ||
                    localStorage.getItem(
                        "user_role"
                    ) ||
                    localStorage.getItem(
                        "user_type"
                    ) ||
                    ""
            )
                .trim()
                .toLowerCase();
        }, []);

    useEffect(() => {
        mountedRef.current =
            true;

        setRole(
            readStoredRole()
        );

        const refreshRole =
            () => {
                if (
                    mountedRef.current
                ) {
                    setRole(
                        readStoredRole()
                    );
                }
            };

        window.addEventListener(
            "storage",
            refreshRole
        );

        window.addEventListener(
            "user-logged-in",
            refreshRole
        );

        window.addEventListener(
            "force-logout",
            refreshRole
        );

        return () => {
            mountedRef.current =
                false;

            window.removeEventListener(
                "storage",
                refreshRole
            );

            window.removeEventListener(
                "user-logged-in",
                refreshRole
            );

            window.removeEventListener(
                "force-logout",
                refreshRole
            );
        };
    }, [readStoredRole]);

    const formatDate =
        useCallback(
            (value) => {
                if (!value) {
                    return "";
                }

                const date =
                    new Date(value);

                if (
                    Number.isNaN(
                        date.getTime()
                    )
                ) {
                    return "";
                }

                try {
                    return new Intl.DateTimeFormat(
                        "en-US",
                        {
                            dateStyle:
                                "medium",
                            timeStyle:
                                "short",
                        }
                    ).format(date);
                } catch {
                    return "";
                }
            },
            []
        );

    const formatFileSize =
        useCallback(
            (bytes) => {
                const size =
                    Number(bytes);

                if (
                    !Number.isFinite(
                        size
                    ) ||
                    size <= 0
                ) {
                    return "File";
                }

                if (size < 1024) {
                    return `${size} B`;
                }

                if (
                    size <
                    1024 * 1024
                ) {
                    return `${(
                        size / 1024
                    ).toFixed(
                        1
                    )} KB`;
                }

                if (
                    size <
                    1024 *
                        1024 *
                        1024
                ) {
                    return `${(
                        size /
                        (1024 *
                            1024)
                    ).toFixed(
                        1
                    )} MB`;
                }

                return `${(
                    size /
                    (1024 *
                        1024 *
                        1024)
                ).toFixed(
                    1
                )} GB`;
            },
            []
        );

    const getErrorMessage =
        useCallback(
            (error, fallback) => {
                const data =
                    error?.response
                        ?.data;

                if (
                    typeof data ===
                    "string"
                ) {
                    return data;
                }

                if (data?.detail) {
                    return data.detail;
                }

                if (data?.message) {
                    return data.message;
                }

                if (data?.error) {
                    return data.error;
                }

                if (data?.question) {
                    return Array.isArray(
                        data.question
                    )
                        ? data.question.join(
                              " "
                          )
                        : data.question;
                }

                if (data?.reply) {
                    return Array.isArray(
                        data.reply
                    )
                        ? data.reply.join(
                              " "
                          )
                        : data.reply;
                }

                if (data?.content) {
                    return Array.isArray(
                        data.content
                    )
                        ? data.content.join(
                              " "
                          )
                        : data.content;
                }

                return fallback;
            },
            []
        );

    const loadClassroom =
        useCallback(
            async (
                showLoader = true
            ) => {
                if (
                    !courseId ||
                    !classroomPath
                ) {
                    if (
                        mountedRef.current
                    ) {
                        setClassroom(
                            EMPTY_CLASSROOM
                        );
                        setLoading(false);
                    }

                    return;
                }

                const requestId =
                    ++classroomRequestRef.current;

                if (
                    showLoader &&
                    mountedRef.current
                ) {
                    setLoading(true);
                }

                try {
                    const response =
                        await api.get(
                            classroomPath
                        );

                    if (
                        !mountedRef.current ||
                        requestId !==
                            classroomRequestRef.current
                    ) {
                        return;
                    }

                    const normalized =
                        normalizeClassroomResponse(
                            response?.data ||
                                {}
                        );

                    setClassroom(
                        normalized
                    );
                } catch (error) {
                    if (
                        !mountedRef.current ||
                        requestId !==
                            classroomRequestRef.current
                    ) {
                        return;
                    }

                    console.error(
                        "Unable to load classroom:",
                        error
                    );

                    setClassroom(
                        EMPTY_CLASSROOM
                    );

                    const status =
                        error?.response
                            ?.status;

                    if (
                        status !== 401 &&
                        status !== 403 &&
                        status !== 404
                    ) {
                        Swal.fire({
                            icon: "error",
                            title:
                                "Unable to Load Classroom",
                            text: getErrorMessage(
                                error,
                                "The classroom information could not be loaded."
                            ),
                            confirmButtonColor:
                                "#062A4B",
                        });
                    }
                } finally {
                    if (
                        mountedRef.current &&
                        requestId ===
                            classroomRequestRef.current &&
                        showLoader
                    ) {
                        setLoading(false);
                    }
                }
            },
            [
                courseId,
                classroomPath,
                normalizeClassroomResponse,
                getErrorMessage,
            ]
        );

    useEffect(() => {
        setActiveTab(
            "overview"
        );

        setDownloadingResourceId(
            null
        );
    }, [
        courseId,
        videoType,
        sectionId,
    ]);

    useEffect(() => {
        loadClassroom(true);
    }, [loadClassroom]);

    const getFileIcon =
        useCallback(
            (resource) => {
                const extension =
                    String(
                        resource?.file_extension ||
                            resource?.extension ||
                            ""
                    ).toLowerCase();

                const contentType =
                    String(
                        resource?.content_type ||
                            resource?.contentType ||
                            ""
                    ).toLowerCase();

                if (
                    extension ===
                        ".pdf" ||
                    contentType.includes(
                        "pdf"
                    )
                ) {
                    return (
                        <FaFilePdf />
                    );
                }

                if (
                    [
                        ".doc",
                        ".docx",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "word"
                    )
                ) {
                    return (
                        <FaFileWord />
                    );
                }

                if (
                    [
                        ".xls",
                        ".xlsx",
                        ".csv",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "excel"
                    ) ||
                    contentType.includes(
                        "spreadsheet"
                    )
                ) {
                    return (
                        <FaFileExcel />
                    );
                }

                if (
                    [
                        ".ppt",
                        ".pptx",
                    ].includes(
                        extension
                    ) ||
                    contentType.includes(
                        "powerpoint"
                    ) ||
                    contentType.includes(
                        "presentation"
                    )
                ) {
                    return (
                        <FaFilePowerpoint />
                    );
                }

                if (
                    extension ===
                    ".txt"
                ) {
                    return (
                        <FaFileAlt />
                    );
                }

                return <FaFile />;
            },
            []
        );

    const getResourceDownloadUrl =
        useCallback(
            (resource) => {
                if (
                    !resource?.id ||
                    !courseId
                ) {
                    return "";
                }

                return (
                    resource?.download_url ||
                    resource?.downloadUrl ||
                    `api/users/subjects/${courseId}/resources/${resource.id}/download/`
                );
            },
            [courseId]
        );

    const downloadResource =
        useCallback(
            async (resource) => {
                const downloadUrl =
                    getResourceDownloadUrl(
                        resource
                    );

                if (
                    !downloadUrl ||
                    !resource?.id
                ) {
                    Swal.fire({
                        icon: "error",
                        title:
                            "Download Unavailable",
                        text:
                            "This learning resource does not have a valid download link.",
                        confirmButtonColor:
                            "#062A4B",
                    });

                    return;
                }

                setDownloadingResourceId(
                    resource.id
                );

                try {
                    const response =
                        await api.get(
                            downloadUrl,
                            {
                                responseType:
                                    "blob",
                            }
                        );

                    const responseType =
                        response
                            ?.headers?.[
                            "content-type"
                        ] ||
                        resource?.content_type ||
                        "application/octet-stream";

                    const blob =
                        new Blob(
                            [
                                response.data,
                            ],
                            {
                                type: responseType,
                            }
                        );

                    const blobUrl =
                        window.URL.createObjectURL(
                            blob
                        );

                    const anchor =
                        document.createElement(
                            "a"
                        );

                    const filename =
                        resource?.original_filename ||
                        resource?.originalFilename ||
                        resource?.title ||
                        "learning-resource";

                    anchor.href =
                        blobUrl;

                    anchor.download =
                        filename;

                    document.body.appendChild(
                        anchor
                    );

                    anchor.click();

                    anchor.remove();

                    window.setTimeout(
                        () => {
                            window.URL.revokeObjectURL(
                                blobUrl
                            );
                        },
                        1000
                    );
                } catch (error) {
                    console.error(
                        "Unable to download resource:",
                        error
                    );

                    Swal.fire({
                        icon: "error",
                        title:
                            "Download Failed",
                        text: getErrorMessage(
                            error,
                            "The learning resource could not be downloaded."
                        ),
                        confirmButtonColor:
                            "#062A4B",
                    });
                } finally {
                    if (
                        mountedRef.current
                    ) {
                        setDownloadingResourceId(
                            null
                        );
                    }
                }
            },
            [
                getResourceDownloadUrl,
                getErrorMessage,
            ]
        );

    const tabs = useMemo(
        () => [
            {
                key: "overview",
                icon: (
                    <FaBookOpen />
                ),
                label: "Overview",
            },
            {
                key: "qa",
                icon: (
                    <FaQuestionCircle />
                ),
                label: "Q&A",
            },
            {
                key: "notes",
                icon: (
                    <span>
                        📝
                    </span>
                ),
                label: "Notes",
            },
            {
                key: "announcements",
                icon: (
                    <FaBullhorn />
                ),
                label: "Announcements",
            },
            {
                key: "reviews",
                icon: <FaStar />,
                label: "Reviews",
            },
            {
                key: "tools",
                icon: <FaTools />,
                label: "Learning Tools",
            },
        ],
        []
    );

    if (!courseId) {
        return (
            <>
                <style>
                    {
                        CLASSROOM_STYLES
                    }
                </style>

                <div className="cayep-classroom-empty">
                    <div className="cayep-classroom-empty-icon">
                        <FaBookOpen />
                    </div>

                    <h3>
                        Classroom
                        Unavailable
                    </h3>

                    <p>
                        Select a course to
                        access the learning
                        environment.
                    </p>
                </div>
            </>
        );
    }

    return (
        <>
            <style>
                {
                    CLASSROOM_STYLES
                }
            </style>

            <div className="cayep-classroom-environment">
                <div className="cayep-classroom-header">
                    <div className="cayep-classroom-header-main">
                        <div className="cayep-classroom-header-icon">
                            <FaGraduationCap />
                        </div>

                        <div className="cayep-classroom-header-copy">
                            <span className="cayep-classroom-eyebrow">
                                {isManagement
                                    ? "FACILITATOR VIEW"
                                    : "LEARNER VIEW"}
                            </span>

                            <h2>
                                {course?.name ||
                                    course?.title ||
                                    "Course Classroom"}
                            </h2>

                            <div className="cayep-classroom-video-context">
                                <FaBookOpen />

                                <span>
                                    {
                                        videoContextLabel
                                    }
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="cayep-classroom-status">
                        <span className="cayep-classroom-status-dot" />

                        <span>
                            {isSectionVideo
                                ? "Section Classroom"
                                : "Course Classroom"}
                        </span>
                    </div>
                </div>

                {isPending && (
                    <div className="cayep-classroom-approval-notice">
                        <div className="cayep-classroom-approval-icon">
                            <FaLock />
                        </div>

                        <div>
                            <strong>
                                Course Access
                                Pending
                            </strong>

                            <p>
                                This course is
                                awaiting
                                approval before
                                classroom
                                content can be
                                accessed.
                            </p>
                        </div>
                    </div>
                )}

                {!isPending && (
                    <>
                        <div className="cayep-classroom-tabs">
                            {tabs.map(
                                (tab) => (
                                    <button
                                        key={
                                            tab.key
                                        }
                                        type="button"
                                        className={
                                            activeTab ===
                                            tab.key
                                                ? "active"
                                                : ""
                                        }
                                        onClick={() =>
                                            setActiveTab(
                                                tab.key
                                            )
                                        }
                                    >
                                        <span className="cayep-classroom-tab-icon">
                                            {
                                                tab.icon
                                            }
                                        </span>

                                        <span className="cayep-classroom-tab-label">
                                            {
                                                tab.label
                                            }
                                        </span>

                                        {tab.key ===
                                            "qa" &&
                                            classroom.questions
                                                .length >
                                                0 && (
                                                <span className="cayep-classroom-tab-count">
                                                    {
                                                        classroom
                                                            .questions
                                                            .length
                                                    }
                                                </span>
                                            )}

                                        {tab.key ===
                                            "tools" &&
                                            classroom.resources
                                                .length >
                                                0 && (
                                                <span className="cayep-classroom-tab-count">
                                                    {
                                                        classroom
                                                            .resources
                                                            .length
                                                    }
                                                </span>
                                            )}
                                    </button>
                                )
                            )}
                        </div>

                        <div className="cayep-classroom-panel">
                            {loading ? (
                                <div className="cayep-classroom-loading">
                                    <div className="cayep-classroom-spinner" />

                                    <span>
                                        Loading
                                        classroom...
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {activeTab ===
                                        "overview" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaBookOpen />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        COURSE
                                                        CONTENT
                                                    </span>

                                                    <h3>
                                                        Overview
                                                    </h3>

                                                    <p>
                                                        Learn
                                                        more about
                                                        this video
                                                        and its
                                                        training
                                                        content.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .overview
                                                ?.content ? (
                                                <div className="cayep-classroom-rich-content">
                                                    {String(
                                                        classroom
                                                            .overview
                                                            .content
                                                    )
                                                        .split(
                                                            /\r?\n/
                                                        )
                                                        .map(
                                                            (
                                                                paragraph,
                                                                index
                                                            ) =>
                                                                paragraph.trim() ? (
                                                                    <p
                                                                        key={
                                                                            index
                                                                        }
                                                                    >
                                                                        {
                                                                            paragraph
                                                                        }
                                                                    </p>
                                                                ) : null
                                                        )}
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaLightbulb />

                                                    <h4>
                                                        No
                                                        Overview
                                                        Yet
                                                    </h4>

                                                    <p>
                                                        The
                                                        facilitator
                                                        has not
                                                        added an
                                                        overview
                                                        for this
                                                        video yet.
                                                    </p>
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "qa" && (
                                        <QuestionAndAnswer
                                            course={
                                                course
                                            }
                                            classroom={
                                                classroom
                                            }
                                            setClassroom={
                                                setClassroom
                                            }
                                            isStudent={
                                                isStudent
                                            }
                                            isManagement={
                                                isManagement
                                            }
                                            videoType={
                                                videoType
                                            }
                                            videoSection={
                                                videoSection
                                            }
                                            videoContextLabel={
                                                videoContextLabel
                                            }
                                            loadClassroom={
                                                loadClassroom
                                            }
                                        />
                                    )}

                                    {activeTab ===
                                        "notes" && (
                                        <StudentsNote
                                            course={
                                                course
                                            }
                                            classroom={
                                                classroom
                                            }
                                            setClassroom={
                                                setClassroom
                                            }
                                            isStudent={
                                                isStudent
                                            }
                                            videoType={
                                                videoType
                                            }
                                            videoSection={
                                                videoSection
                                            }
                                            loadClassroom={
                                                loadClassroom
                                            }
                                        />
                                    )}

                                    {activeTab ===
                                        "announcements" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaBullhorn />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        UPDATES
                                                    </span>

                                                    <h3>
                                                        Announcements
                                                    </h3>

                                                    <p>
                                                        Important
                                                        information
                                                        from the
                                                        facilitator
                                                        for this
                                                        video.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .announcements
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaBullhorn />

                                                    <h4>
                                                        No
                                                        Announcements
                                                    </h4>

                                                    <p>
                                                        There are
                                                        no
                                                        announcements
                                                        for this
                                                        video at
                                                        the moment.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-announcement-list">
                                                    {classroom.announcements.map(
                                                        (
                                                            announcement,
                                                            index
                                                        ) => (
                                                            <article
                                                                className="cayep-classroom-announcement-card"
                                                                key={
                                                                    announcement?.id ||
                                                                    index
                                                                }
                                                            >
                                                                <div className="cayep-classroom-announcement-icon">
                                                                    <FaBullhorn />
                                                                </div>

                                                                <div className="cayep-classroom-announcement-body">
                                                                    <div className="cayep-classroom-announcement-meta">
                                                                        <span>
                                                                            {formatDate(
                                                                                announcement?.created_at ||
                                                                                    announcement?.createdAt
                                                                            )}
                                                                        </span>
                                                                    </div>

                                                                    <h4>
                                                                        {
                                                                            announcement?.title
                                                                        }
                                                                    </h4>

                                                                    <p>
                                                                        {
                                                                            announcement?.content
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </article>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "reviews" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaStar />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        FACILITATOR
                                                        FEEDBACK
                                                    </span>

                                                    <h3>
                                                        Reviews
                                                    </h3>

                                                    <p>
                                                        Guidance
                                                        and
                                                        feedback
                                                        associated
                                                        with this
                                                        video.
                                                    </p>
                                                </div>
                                            </div>

                                            {classroom
                                                .reviews
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaStar />

                                                    <h4>
                                                        No Reviews
                                                        Yet
                                                    </h4>

                                                    <p>
                                                        No
                                                        facilitator
                                                        review has
                                                        been added
                                                        for this
                                                        video yet.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-review-list">
                                                    {classroom.reviews.map(
                                                        (
                                                            review,
                                                            index
                                                        ) => (
                                                            <article
                                                                className="cayep-classroom-review-card"
                                                                key={
                                                                    review?.id ||
                                                                    index
                                                                }
                                                            >
                                                                <div className="cayep-classroom-review-header">
                                                                    <div className="cayep-classroom-review-icon">
                                                                        <FaStar />
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            Facilitator
                                                                            Review
                                                                        </strong>

                                                                        <span>
                                                                            {formatDate(
                                                                                review?.created_at ||
                                                                                    review?.createdAt
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <p>
                                                                    {
                                                                        review?.content
                                                                    }
                                                                </p>
                                                            </article>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {activeTab ===
                                        "tools" && (
                                        <section className="cayep-classroom-content-section">
                                            <div className="cayep-classroom-section-heading">
                                                <div className="cayep-classroom-heading-icon">
                                                    <FaTools />
                                                </div>

                                                <div>
                                                    <span className="cayep-classroom-section-kicker">
                                                        COURSE
                                                        MATERIALS
                                                    </span>

                                                    <h3>
                                                        Learning
                                                        Tools
                                                    </h3>

                                                    <p>
                                                        Download
                                                        learning
                                                        materials
                                                        provided
                                                        specifically
                                                        for this
                                                        video.
                                                    </p>

                                                    <div className="cayep-classroom-context-pill">
                                                        <FaBookOpen />

                                                        <span>
                                                            {
                                                                videoContextLabel
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {classroom
                                                .resources
                                                .length ===
                                            0 ? (
                                                <div className="cayep-classroom-empty-panel">
                                                    <FaTools />

                                                    <h4>
                                                        No Learning
                                                        Resources
                                                    </h4>

                                                    <p>
                                                        {isSectionVideo
                                                            ? "No files have been uploaded for this section yet."
                                                            : "No files have been uploaded for the Introduction Video yet."}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="cayep-classroom-resource-list">
                                                    {classroom.resources.map(
                                                        (
                                                            resource,
                                                            index
                                                        ) => {
                                                            const filename =
                                                                resource?.original_filename ||
                                                                resource?.originalFilename ||
                                                                resource?.title ||
                                                                "Learning Resource";

                                                            const extension =
                                                                resource?.file_extension ||
                                                                resource?.extension ||
                                                                "";

                                                            const fileSize =
                                                                formatFileSize(
                                                                    resource?.file_size ??
                                                                        resource?.fileSize
                                                                );

                                                            const isDownloading =
                                                                String(
                                                                    downloadingResourceId
                                                                ) ===
                                                                String(
                                                                    resource?.id
                                                                );

                                                            return (
                                                                <article
                                                                    className="cayep-classroom-resource-card"
                                                                    key={
                                                                        resource?.id ||
                                                                        index
                                                                    }
                                                                >
                                                                    <div className="cayep-classroom-resource-icon">
                                                                        {getFileIcon(
                                                                            resource
                                                                        )}
                                                                    </div>

                                                                    <div className="cayep-classroom-resource-info">
                                                                        <div className="cayep-classroom-resource-top">
                                                                            <h4>
                                                                                {
                                                                                    resource?.title
                                                                                }
                                                                            </h4>

                                                                            {extension && (
                                                                                <span className="cayep-classroom-resource-extension">
                                                                                    {String(
                                                                                        extension
                                                                                    )
                                                                                        .replace(
                                                                                            ".",
                                                                                            ""
                                                                                        )
                                                                                        .toUpperCase()}
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        <p className="cayep-classroom-resource-filename">
                                                                            {
                                                                                filename
                                                                            }
                                                                        </p>

                                                                        {resource?.description && (
                                                                            <p className="cayep-classroom-resource-description">
                                                                                {
                                                                                    resource.description
                                                                                }
                                                                            </p>
                                                                        )}

                                                                        <div className="cayep-classroom-resource-meta">
                                                                            <span>
                                                                                {
                                                                                    fileSize
                                                                                }
                                                                            </span>

                                                                            {resource?.created_at && (
                                                                                <>
                                                                                    <span className="cayep-resource-meta-dot">
                                                                                        •
                                                                                    </span>

                                                                                    <span>
                                                                                        Uploaded{" "}
                                                                                        {formatDate(
                                                                                            resource.created_at
                                                                                        )}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        className="cayep-classroom-resource-download"
                                                                        onClick={() =>
                                                                            downloadResource(
                                                                                resource
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isDownloading
                                                                        }
                                                                    >
                                                                        <FaDownload />

                                                                        <span>
                                                                            {isDownloading
                                                                                ? "Downloading..."
                                                                                : "Download"}
                                                                        </span>
                                                                    </button>
                                                                </article>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default ClassRoomEnvironment;




// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useRef,
//     useState,
// } from "react";

// import {
//     FaBookOpen,
//     FaBullhorn,
//     FaCheckCircle,
//     FaDownload,
//     FaFile,
//     FaFileAlt,
//     FaFileExcel,
//     FaFilePdf,
//     FaFilePowerpoint,
//     FaFileWord,
//     FaGraduationCap,
//     FaLightbulb,
//     FaLock,
//     FaPen,
//     FaSave,
//     FaStar,
//     FaStickyNote,
//     FaQuestionCircle,
//     FaTools,
// } from "react-icons/fa";

// import Swal from "sweetalert2";

// import api from "../api/api";
// import defaultAvatar from "./learningenvironment.jpg";
// import QuestionAndAnswer from "./QuestionAndAnswer";

// const CLASSROOM_STYLES = `
// .cayep-classroom-environment {
//     width: 100%;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     overflow: hidden;
//     color: #142536;
// }

// .cayep-classroom-empty {
//     width: 100%;
//     min-height: 320px;
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     padding: 40px 20px;
//     text-align: center;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     color: #142536;
// }

// .cayep-classroom-empty-icon {
//     width: 64px;
//     height: 64px;
//     border-radius: 16px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 26px;
//     margin-bottom: 16px;
// }

// .cayep-classroom-empty h3 {
//     margin: 0 0 8px;
//     color: #062A4B;
//     font-size: 20px;
//     font-weight: 800;
// }

// .cayep-classroom-empty p {
//     margin: 0;
//     color: #6B7280;
//     font-size: 13px;
// }

// .cayep-classroom-header {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 20px;
//     padding: 22px;
//     background: linear-gradient(
//         135deg,
//         #062A4B 0%,
//         #063A6B 100%
//     );
//     color: #FFFFFF;
// }

// .cayep-classroom-header-main {
//     display: flex;
//     align-items: center;
//     gap: 15px;
//     min-width: 0;
// }

// .cayep-classroom-header-icon {
//     width: 52px;
//     height: 52px;
//     min-width: 52px;
//     border-radius: 14px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: rgba(246, 201, 68, 0.15);
//     border: 1px solid rgba(246, 201, 68, 0.3);
//     color: #F6C944;
//     font-size: 22px;
// }

// .cayep-classroom-header-copy {
//     min-width: 0;
// }

// .cayep-classroom-eyebrow {
//     display: block;
//     margin-bottom: 5px;
//     color: #F6C944;
//     font-size: 10px;
//     font-weight: 800;
//     letter-spacing: 1.2px;
// }

// .cayep-classroom-header-copy h2 {
//     margin: 0;
//     color: #FFFFFF;
//     font-size: 21px;
//     font-weight: 800;
//     line-height: 1.25;
// }

// .cayep-classroom-video-context {
//     display: flex;
//     align-items: center;
//     gap: 7px;
//     margin-top: 8px;
//     color: rgba(255, 255, 255, 0.75);
//     font-size: 12px;
// }

// .cayep-classroom-video-context svg {
//     color: #F6C944;
// }

// .cayep-classroom-status {
//     display: inline-flex;
//     align-items: center;
//     gap: 8px;
//     flex-shrink: 0;
//     padding: 8px 11px;
//     border: 1px solid rgba(255, 255, 255, 0.18);
//     border-radius: 999px;
//     background: rgba(255, 255, 255, 0.08);
//     color: rgba(255, 255, 255, 0.9);
//     font-size: 11px;
//     font-weight: 700;
// }

// .cayep-classroom-status-dot {
//     width: 7px;
//     height: 7px;
//     border-radius: 50%;
//     background: #4FAE22;
//     box-shadow: 0 0 0 3px rgba(79, 174, 34, 0.15);
// }

// .cayep-classroom-approval-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     margin: 18px;
//     padding: 15px;
//     background: #FFF9E8;
//     border: 1px solid #F1D985;
//     border-radius: 11px;
// }

// .cayep-classroom-approval-icon {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 10px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #FFF1C2;
//     color: #C0750D;
// }

// .cayep-classroom-approval-notice strong {
//     display: block;
//     margin-bottom: 4px;
//     color: #6B4B00;
//     font-size: 13px;
//     font-weight: 800;
// }

// .cayep-classroom-approval-notice p {
//     margin: 0;
//     color: #806A32;
//     font-size: 12px;
//     line-height: 1.5;
// }

// .cayep-classroom-tabs {
//     display: flex;
//     align-items: stretch;
//     gap: 2px;
//     padding: 8px;
//     background: #F7F9FB;
//     border-bottom: 1px solid #E0E7EC;
//     overflow-x: auto;
// }

// .cayep-classroom-tabs button {
//     position: relative;
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     gap: 7px;
//     min-height: 40px;
//     padding: 8px 13px;
//     border: 0;
//     border-radius: 8px;
//     background: transparent;
//     color: #687684;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 12px;
//     font-weight: 700;
//     white-space: nowrap;
//     transition: all 0.2s ease;
// }

// .cayep-classroom-tabs button:hover {
//     background: #EEF3F7;
//     color: #063A6B;
// }

// .cayep-classroom-tabs button.active {
//     background: #FFFFFF;
//     color: #062A4B;
//     box-shadow: 0 2px 8px rgba(6, 42, 75, 0.08);
// }

// .cayep-classroom-tab-icon {
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     font-size: 13px;
// }

// .cayep-classroom-tabs button.active
// .cayep-classroom-tab-icon {
//     color: #C0750D;
// }

// .cayep-classroom-tab-count {
//     min-width: 19px;
//     height: 19px;
//     padding: 0 5px;
//     border-radius: 999px;
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 9px;
//     font-weight: 800;
// }

// .cayep-classroom-panel {
//     min-height: 400px;
//     background: #FFFFFF;
// }

// .cayep-classroom-loading {
//     min-height: 400px;
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     gap: 12px;
//     color: #7A8793;
//     font-size: 12px;
// }

// .cayep-classroom-spinner {
//     width: 30px;
//     height: 30px;
//     border: 3px solid #E5ECF1;
//     border-top-color: #0F6DAB;
//     border-radius: 50%;
//     animation: cayep-classroom-spin 0.8s linear infinite;
// }

// @keyframes cayep-classroom-spin {
//     to {
//         transform: rotate(360deg);
//     }
// }

// .cayep-classroom-content-section {
//     padding: 25px;
// }

// .cayep-classroom-section-heading {
//     display: flex;
//     align-items: flex-start;
//     gap: 14px;
//     margin-bottom: 22px;
// }

// .cayep-classroom-heading-icon {
//     width: 42px;
//     height: 42px;
//     min-width: 42px;
//     border-radius: 11px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 17px;
// }

// .cayep-classroom-section-kicker {
//     display: block;
//     margin-bottom: 5px;
//     color: #0F6DAB;
//     font-size: 9px;
//     font-weight: 800;
//     letter-spacing: 1px;
// }

// .cayep-classroom-section-heading h3 {
//     margin: 0 0 5px;
//     color: #062A4B;
//     font-size: 20px;
//     font-weight: 800;
// }

// .cayep-classroom-section-heading p {
//     margin: 0;
//     color: #6B7280;
//     font-size: 12px;
//     line-height: 1.5;
// }

// .cayep-classroom-context-pill {
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     margin-top: 10px;
//     padding: 6px 9px;
//     border-radius: 7px;
//     background: #F4F7F9;
//     border: 1px solid #E0E7EC;
//     color: #526273;
//     font-size: 10px;
//     font-weight: 700;
// }

// .cayep-classroom-context-pill svg {
//     color: #C0750D;
// }

// .cayep-classroom-rich-content {
//     padding: 18px;
//     background: #F7F9FB;
//     border: 1px solid #E0E7EC;
//     border-radius: 11px;
// }

// .cayep-classroom-rich-content p {
//     margin: 0 0 12px;
//     color: #374757;
//     font-size: 13px;
//     line-height: 1.75;
// }

// .cayep-classroom-rich-content p:last-child {
//     margin-bottom: 0;
// }

// .cayep-classroom-empty-panel {
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     min-height: 220px;
//     padding: 30px;
//     text-align: center;
//     background: #F7F9FB;
//     border: 1px dashed #D5DEE5;
//     border-radius: 12px;
// }

// .cayep-classroom-empty-panel > svg {
//     margin-bottom: 12px;
//     color: #0F6DAB;
//     font-size: 27px;
// }

// .cayep-classroom-empty-panel h4 {
//     margin: 0 0 6px;
//     color: #062A4B;
//     font-size: 15px;
//     font-weight: 800;
// }

// .cayep-classroom-empty-panel p {
//     max-width: 500px;
//     margin: 0;
//     color: #6B7280;
//     font-size: 12px;
//     line-height: 1.6;
// }

// .cayep-classroom-notes-form {
//     overflow: hidden;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 12px;
// }

// .cayep-classroom-notes-toolbar {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     padding: 12px 15px;
//     background: #F7F9FB;
//     border-bottom: 1px solid #E0E7EC;
// }

// .cayep-classroom-notes-toolbar > span:first-child {
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     color: #062A4B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-notes-toolbar svg {
//     color: #C0750D;
// }

// .cayep-classroom-note-saved {
//     display: inline-flex;
//     align-items: center;
//     gap: 5px;
//     color: #4FAE22;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-classroom-note-saved svg {
//     color: #4FAE22;
// }

// .cayep-classroom-notes-form textarea {
//     width: 100%;
//     min-height: 280px;
//     padding: 16px;
//     border: 0;
//     outline: none;
//     resize: vertical;
//     box-sizing: border-box;
//     color: #142536;
//     background: #FFFFFF;
//     font-family: inherit;
//     font-size: 13px;
//     line-height: 1.7;
// }

// .cayep-classroom-notes-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 12px;
//     padding: 12px 15px;
//     border-top: 1px solid #E0E7EC;
// }

// .cayep-classroom-notes-footer > span {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     color: #7A8793;
//     font-size: 10px;
// }

// .cayep-classroom-notes-footer button {
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     border: 0;
//     border-radius: 8px;
//     padding: 9px 13px;
//     background: #062A4B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-classroom-notes-footer button:hover {
//     background: #063A6B;
// }

// .cayep-classroom-notes-footer button:disabled {
//     opacity: 0.55;
//     cursor: not-allowed;
// }

// .cayep-classroom-announcement-list,
// .cayep-classroom-review-list,
// .cayep-classroom-resource-list {
//     display: flex;
//     flex-direction: column;
//     gap: 10px;
// }

// .cayep-classroom-announcement-card,
// .cayep-classroom-review-card,
// .cayep-classroom-resource-card {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     padding: 15px;
//     background: #FFFFFF;
//     border: 1px solid #E0E7EC;
//     border-radius: 11px;
// }

// .cayep-classroom-announcement-icon,
// .cayep-classroom-review-icon,
// .cayep-classroom-resource-icon {
//     width: 38px;
//     height: 38px;
//     min-width: 38px;
//     border-radius: 9px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     background: #EEF5FA;
//     color: #0F6DAB;
// }

// .cayep-classroom-announcement-body,
// .cayep-classroom-resource-info {
//     flex: 1;
//     min-width: 0;
// }

// .cayep-classroom-announcement-meta {
//     margin-bottom: 4px;
//     color: #7A8793;
//     font-size: 10px;
// }

// .cayep-classroom-announcement-body h4 {
//     margin: 0 0 5px;
//     color: #062A4B;
//     font-size: 14px;
//     font-weight: 800;
// }

// .cayep-classroom-announcement-body p {
//     margin: 0;
//     color: #596978;
//     font-size: 12px;
//     line-height: 1.6;
//     white-space: pre-wrap;
// }

// .cayep-classroom-review-header {
//     display: flex;
//     align-items: center;
//     gap: 10px;
// }

// .cayep-classroom-review-header strong {
//     display: block;
//     color: #062A4B;
//     font-size: 12px;
// }

// .cayep-classroom-review-header span {
//     display: block;
//     margin-top: 2px;
//     color: #7A8793;
//     font-size: 10px;
// }

// .cayep-classroom-review-card > p {
//     flex: 1;
//     margin: 0;
//     color: #596978;
//     font-size: 12px;
//     line-height: 1.65;
//     white-space: pre-wrap;
// }

// .cayep-classroom-resource-top {
//     display: flex;
//     align-items: center;
//     gap: 8px;
// }

// .cayep-classroom-resource-top h4 {
//     margin: 0;
//     color: #062A4B;
//     font-size: 13px;
//     font-weight: 800;
//     overflow: hidden;
//     text-overflow: ellipsis;
//     white-space: nowrap;
// }

// .cayep-classroom-resource-extension {
//     padding: 3px 6px;
//     border-radius: 5px;
//     background: #EEF5FA;
//     color: #0F6DAB;
//     font-size: 8px;
//     font-weight: 800;
//     flex-shrink: 0;
// }

// .cayep-classroom-resource-filename {
//     margin: 3px 0 0;
//     color: #7A8793;
//     font-size: 10px;
//     overflow: hidden;
//     text-overflow: ellipsis;
//     white-space: nowrap;
// }

// .cayep-classroom-resource-description {
//     margin: 5px 0 0;
//     color: #596978;
//     font-size: 11px;
//     line-height: 1.5;
// }

// .cayep-classroom-resource-meta {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 6px;
//     margin-top: 7px;
//     color: #7A8793;
//     font-size: 9px;
// }

// .cayep-resource-meta-dot {
//     color: #C4C7C7;
// }

// .cayep-classroom-resource-download {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     flex-shrink: 0;
//     border: 0;
//     border-radius: 7px;
//     padding: 8px 10px;
//     background: #EEF3F7;
//     color: #063A6B;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-classroom-resource-download:hover {
//     background: #DDEAF2;
// }

// .cayep-classroom-resource-download:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
// }

// @media (max-width: 760px) {
//     .cayep-classroom-header {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-classroom-status {
//         align-self: flex-start;
//     }

//     .cayep-classroom-tabs {
//         scrollbar-width: thin;
//     }

//     .cayep-classroom-content-section {
//         padding: 18px;
//     }

//     .cayep-classroom-resource-card {
//         flex-wrap: wrap;
//     }

//     .cayep-classroom-resource-download {
//         width: 100%;
//         justify-content: center;
//     }
// }

// @media (max-width: 480px) {
//     .cayep-classroom-header {
//         padding: 17px;
//     }

//     .cayep-classroom-header-icon {
//         width: 44px;
//         height: 44px;
//         min-width: 44px;
//     }

//     .cayep-classroom-header-copy h2 {
//         font-size: 18px;
//     }

//     .cayep-classroom-content-section {
//         padding: 14px;
//     }

//     .cayep-classroom-section-heading {
//         gap: 10px;
//     }

//     .cayep-classroom-heading-icon {
//         width: 36px;
//         height: 36px;
//         min-width: 36px;
//     }

//     .cayep-classroom-section-heading h3 {
//         font-size: 17px;
//     }

//     .cayep-classroom-notes-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-classroom-notes-footer button {
//         width: 100%;
//         justify-content: center;
//     }

//     .cayep-classroom-announcement-card,
//     .cayep-classroom-review-card {
//         padding: 12px;
//     }
// }
// `;

// const EMPTY_CLASSROOM = {
//     overview: null,
//     questions: [],
//     notes: null,
//     announcements: [],
//     reviews: [],
//     resources: [],
// };

// const MANAGEMENT_ROLES = new Set([
//     "teacher",
//     "facilitator",
//     "instructor",
//     "admin",
//     "administrator",
//     "principal",
//     "proprietor",
// ]);

// const ClassRoomEnvironment = ({
//     course,
//     videoType = "introduction",
//     videoSection = null,
// }) => {
//     const [activeTab, setActiveTab] =
//         useState("overview");

//     const [classroom, setClassroom] =
//         useState(EMPTY_CLASSROOM);

//     const [loading, setLoading] =
//         useState(true);

//     const [noteContent, setNoteContent] =
//         useState("");

//     const [noteSaved, setNoteSaved] =
//         useState(false);

//     const [savingNote, setSavingNote] =
//         useState(false);

//     const [
//         downloadingResourceId,
//         setDownloadingResourceId,
//     ] = useState(null);

//     const [role, setRole] =
//         useState("");

//     const mountedRef =
//         useRef(true);

//     const classroomRequestRef =
//         useRef(0);

//     const noteSavedTimerRef =
//         useRef(null);

//     const courseId = course?.id;

//     const isStudent =
//         role === "student";

//     const isManagement =
//         MANAGEMENT_ROLES.has(role);

//     const isSectionVideo =
//         videoType === "section" &&
//         Boolean(
//             videoSection?.id ||
//                 videoSection?.section_id ||
//                 videoSection?.sectionId
//         );

//     const sectionId = useMemo(() => {
//         if (!isSectionVideo) {
//             return null;
//         }

//         return (
//             videoSection?.id ||
//             videoSection?.section_id ||
//             videoSection?.sectionId ||
//             null
//         );
//     }, [
//         isSectionVideo,
//         videoSection,
//     ]);

//     const learningStatus =
//         useMemo(() => {
//             const value =
//                 course?.learning_status ??
//                 course?.learningStatus ??
//                 course?.approval_status ??
//                 course?.approvalStatus ??
//                 "";

//             return String(value)
//                 .trim()
//                 .toLowerCase();
//         }, [course]);

//     const hasExplicitLearningStatus =
//         Boolean(learningStatus);

//     const isApproved =
//         !hasExplicitLearningStatus ||
//         learningStatus === "approved" ||
//         learningStatus === "active" ||
//         learningStatus === "published";

//     const isPending =
//         Boolean(courseId) &&
//         hasExplicitLearningStatus &&
//         !isApproved;

//     const videoContextLabel =
//         useMemo(() => {
//             if (!isSectionVideo) {
//                 return "Introduction Video";
//             }

//             const number =
//                 videoSection?.section_number ||
//                 videoSection?.sectionNumber ||
//                 "";

//             const title =
//                 videoSection?.title ||
//                 videoSection?.name ||
//                 "Course Section";

//             if (number) {
//                 return `Section ${number} • ${title}`;
//             }

//             return title;
//         }, [
//             isSectionVideo,
//             videoSection,
//         ]);

//     const classroomPath =
//         useMemo(() => {
//             if (!courseId) {
//                 return "";
//             }

//             if (
//                 isSectionVideo &&
//                 sectionId
//             ) {
//                 return `api/users/subjects/${courseId}/video-sections/${sectionId}/classroom/`;
//             }

//             return `api/users/subjects/${courseId}/classroom/`;
//         }, [
//             courseId,
//             isSectionVideo,
//             sectionId,
//         ]);

//     const notesPath =
//         useMemo(() => {
//             if (!courseId) {
//                 return "";
//             }

//             if (
//                 isSectionVideo &&
//                 sectionId
//             ) {
//                 return `api/users/subjects/${courseId}/video-sections/${sectionId}/notes/`;
//             }

//             return `api/users/subjects/${courseId}/notes/`;
//         }, [
//             courseId,
//             isSectionVideo,
//             sectionId,
//         ]);

//     const getArray =
//         useCallback(
//             (value) => {
//                 if (
//                     Array.isArray(value)
//                 ) {
//                     return value;
//                 }

//                 if (
//                     Array.isArray(
//                         value?.results
//                     )
//                 ) {
//                     return value.results;
//                 }

//                 if (
//                     Array.isArray(
//                         value?.data
//                     )
//                 ) {
//                     return value.data;
//                 }

//                 return [];
//             },
//             []
//         );

//     const normalizeClassroomResponse =
//         useCallback(
//             (data) => {
//                 const source =
//                     data?.data &&
//                     typeof data.data ===
//                         "object" &&
//                     !Array.isArray(
//                         data.data
//                     )
//                         ? data.data
//                         : data || {};

//                 return {
//                     overview:
//                         source?.overview ||
//                         source?.course_overview ||
//                         source?.video_section_overview ||
//                         null,

//                     questions:
//                         getArray(
//                             source?.questions ||
//                                 source?.qa ||
//                                 source?.question_and_answers
//                         ),

//                     notes:
//                         source?.notes ||
//                         source?.note ||
//                         null,

//                     announcements:
//                         getArray(
//                             source?.announcements
//                         ),

//                     reviews:
//                         getArray(
//                             source?.reviews
//                         ),

//                     resources:
//                         getArray(
//                             source?.resources ||
//                                 source?.learning_resources ||
//                                 source?.course_resources
//                         ),
//                 };
//             },
//             [getArray]
//         );

//     const readStoredRole =
//         useCallback(() => {
//             try {
//                 const storedUser =
//                     localStorage.getItem(
//                         "user"
//                     );

//                 if (storedUser) {
//                     const parsedUser =
//                         JSON.parse(
//                             storedUser
//                         );

//                     const storedRole =
//                         parsedUser?.user_type ||
//                         parsedUser?.role ||
//                         parsedUser?.user_role ||
//                         "";

//                     if (
//                         storedRole
//                     ) {
//                         return String(
//                             storedRole
//                         )
//                             .trim()
//                             .toLowerCase();
//                     }
//                 }
//             } catch (error) {
//                 console.warn(
//                     "Unable to read stored user:",
//                     error
//                 );
//             }

//             return String(
//                 localStorage.getItem(
//                     "role"
//                 ) ||
//                     localStorage.getItem(
//                         "user_role"
//                     ) ||
//                     localStorage.getItem(
//                         "user_type"
//                     ) ||
//                     ""
//             )
//                 .trim()
//                 .toLowerCase();
//         }, []);

//     useEffect(() => {
//         mountedRef.current =
//             true;

//         setRole(
//             readStoredRole()
//         );

//         const refreshRole =
//             () => {
//                 if (
//                     mountedRef.current
//                 ) {
//                     setRole(
//                         readStoredRole()
//                     );
//                 }
//             };

//         window.addEventListener(
//             "storage",
//             refreshRole
//         );

//         window.addEventListener(
//             "user-logged-in",
//             refreshRole
//         );

//         window.addEventListener(
//             "force-logout",
//             refreshRole
//         );

//         return () => {
//             mountedRef.current =
//                 false;

//             window.removeEventListener(
//                 "storage",
//                 refreshRole
//             );

//             window.removeEventListener(
//                 "user-logged-in",
//                 refreshRole
//             );

//             window.removeEventListener(
//                 "force-logout",
//                 refreshRole
//             );

//             if (
//                 noteSavedTimerRef.current
//             ) {
//                 clearTimeout(
//                     noteSavedTimerRef.current
//                 );
//             }
//         };
//     }, [readStoredRole]);

//     const formatDate =
//         useCallback(
//             (value) => {
//                 if (!value) {
//                     return "";
//                 }

//                 const date =
//                     new Date(value);

//                 if (
//                     Number.isNaN(
//                         date.getTime()
//                     )
//                 ) {
//                     return "";
//                 }

//                 try {
//                     return new Intl.DateTimeFormat(
//                         "en-US",
//                         {
//                             dateStyle:
//                                 "medium",
//                             timeStyle:
//                                 "short",
//                         }
//                     ).format(date);
//                 } catch {
//                     return "";
//                 }
//             },
//             []
//         );

//     const formatFileSize =
//         useCallback(
//             (bytes) => {
//                 const size =
//                     Number(bytes);

//                 if (
//                     !Number.isFinite(
//                         size
//                     ) ||
//                     size <= 0
//                 ) {
//                     return "File";
//                 }

//                 if (size < 1024) {
//                     return `${size} B`;
//                 }

//                 if (
//                     size <
//                     1024 * 1024
//                 ) {
//                     return `${(
//                         size / 1024
//                     ).toFixed(
//                         1
//                     )} KB`;
//                 }

//                 if (
//                     size <
//                     1024 *
//                         1024 *
//                         1024
//                 ) {
//                     return `${(
//                         size /
//                         (1024 *
//                             1024)
//                     ).toFixed(
//                         1
//                     )} MB`;
//                 }

//                 return `${(
//                     size /
//                     (1024 *
//                         1024 *
//                         1024)
//                 ).toFixed(
//                     1
//                 )} GB`;
//             },
//             []
//         );

//     const getErrorMessage =
//         useCallback(
//             (error, fallback) => {
//                 const data =
//                     error?.response
//                         ?.data;

//                 if (
//                     typeof data ===
//                     "string"
//                 ) {
//                     return data;
//                 }

//                 if (data?.detail) {
//                     return data.detail;
//                 }

//                 if (data?.message) {
//                     return data.message;
//                 }

//                 if (data?.error) {
//                     return data.error;
//                 }

//                 if (data?.question) {
//                     return Array.isArray(
//                         data.question
//                     )
//                         ? data.question.join(
//                               " "
//                           )
//                         : data.question;
//                 }

//                 if (data?.reply) {
//                     return Array.isArray(
//                         data.reply
//                     )
//                         ? data.reply.join(
//                               " "
//                           )
//                         : data.reply;
//                 }

//                 if (data?.content) {
//                     return Array.isArray(
//                         data.content
//                     )
//                         ? data.content.join(
//                               " "
//                           )
//                         : data.content;
//                 }

//                 return fallback;
//             },
//             []
//         );

//     const loadClassroom =
//         useCallback(
//             async (
//                 showLoader = true
//             ) => {
//                 if (
//                     !courseId ||
//                     !classroomPath
//                 ) {
//                     if (
//                         mountedRef.current
//                     ) {
//                         setClassroom(
//                             EMPTY_CLASSROOM
//                         );
//                         setLoading(false);
//                     }

//                     return;
//                 }

//                 const requestId =
//                     ++classroomRequestRef.current;

//                 if (
//                     showLoader &&
//                     mountedRef.current
//                 ) {
//                     setLoading(true);
//                 }

//                 try {
//                     const response =
//                         await api.get(
//                             classroomPath
//                         );

//                     if (
//                         !mountedRef.current ||
//                         requestId !==
//                             classroomRequestRef.current
//                     ) {
//                         return;
//                     }

//                     const normalized =
//                         normalizeClassroomResponse(
//                             response?.data ||
//                                 {}
//                         );

//                     setClassroom(
//                         normalized
//                     );

//                     setNoteContent(
//                         normalized
//                             .notes
//                             ?.content ||
//                             ""
//                     );

//                     setNoteSaved(false);
//                 } catch (error) {
//                     if (
//                         !mountedRef.current ||
//                         requestId !==
//                             classroomRequestRef.current
//                     ) {
//                         return;
//                     }

//                     console.error(
//                         "Unable to load classroom:",
//                         error
//                     );

//                     setClassroom(
//                         EMPTY_CLASSROOM
//                     );

//                     setNoteContent(
//                         ""
//                     );

//                     const status =
//                         error?.response
//                             ?.status;

//                     if (
//                         status !== 401 &&
//                         status !== 403 &&
//                         status !== 404
//                     ) {
//                         Swal.fire({
//                             icon: "error",
//                             title:
//                                 "Unable to Load Classroom",
//                             text: getErrorMessage(
//                                 error,
//                                 "The classroom information could not be loaded."
//                             ),
//                             confirmButtonColor:
//                                 "#062A4B",
//                         });
//                     }
//                 } finally {
//                     if (
//                         mountedRef.current &&
//                         requestId ===
//                             classroomRequestRef.current &&
//                         showLoader
//                     ) {
//                         setLoading(false);
//                     }
//                 }
//             },
//             [
//                 courseId,
//                 classroomPath,
//                 normalizeClassroomResponse,
//                 getErrorMessage,
//             ]
//         );

//     useEffect(() => {
//         setActiveTab(
//             "overview"
//         );
//         setNoteContent("");
//         setNoteSaved(false);
//         setSavingNote(false);
//         setDownloadingResourceId(
//             null
//         );
//     }, [
//         courseId,
//         videoType,
//         sectionId,
//     ]);

//     useEffect(() => {
//         loadClassroom(true);
//     }, [loadClassroom]);

//     const saveNote =
//         useCallback(
//             async (event) => {
//                 event.preventDefault();

//                 if (
//                     !isStudent ||
//                     !notesPath ||
//                     savingNote
//                 ) {
//                     return;
//                 }

//                 setSavingNote(
//                     true
//                 );

//                 setNoteSaved(
//                     false
//                 );

//                 const content =
//                     noteContent;

//                 try {
//                     const existingNote =
//                         classroom?.notes;

//                     let response;

//                     if (
//                         existingNote?.id
//                     ) {
//                         response =
//                             await api.patch(
//                                 notesPath,
//                                 {
//                                     content,
//                                 }
//                             );
//                     } else {
//                         response =
//                             await api.post(
//                                 notesPath,
//                                 {
//                                     content,
//                                 }
//                             );
//                     }

//                     const savedNote =
//                         response?.data;

//                     setClassroom(
//                         (
//                             previous
//                         ) => ({
//                             ...previous,
//                             notes:
//                                 savedNote ||
//                                 {
//                                     ...previous.notes,
//                                     content,
//                                 },
//                         })
//                     );

//                     setNoteContent(
//                         savedNote?.content ??
//                             content
//                     );

//                     setNoteSaved(
//                         true
//                     );

//                     if (
//                         noteSavedTimerRef.current
//                     ) {
//                         clearTimeout(
//                             noteSavedTimerRef.current
//                         );
//                     }

//                     noteSavedTimerRef.current =
//                         window.setTimeout(
//                             () => {
//                                 if (
//                                     mountedRef.current
//                                 ) {
//                                     setNoteSaved(
//                                         false
//                                     );
//                                 }
//                             },
//                             2500
//                         );
//                 } catch (error) {
//                     console.error(
//                         "Unable to save note:",
//                         error
//                     );

//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Note Not Saved",
//                         text: getErrorMessage(
//                             error,
//                             "Your private note could not be saved."
//                         ),
//                         confirmButtonColor:
//                             "#062A4B",
//                     });
//                 } finally {
//                     if (
//                         mountedRef.current
//                     ) {
//                         setSavingNote(
//                             false
//                         );
//                     }
//                 }
//             },
//             [
//                 isStudent,
//                 notesPath,
//                 savingNote,
//                 noteContent,
//                 classroom?.notes,
//                 getErrorMessage,
//             ]
//         );

//     const getFileIcon =
//         useCallback(
//             (resource) => {
//                 const extension =
//                     String(
//                         resource?.file_extension ||
//                             resource?.extension ||
//                             ""
//                     ).toLowerCase();

//                 const contentType =
//                     String(
//                         resource?.content_type ||
//                             resource?.contentType ||
//                             ""
//                     ).toLowerCase();

//                 if (
//                     extension ===
//                         ".pdf" ||
//                     contentType.includes(
//                         "pdf"
//                     )
//                 ) {
//                     return (
//                         <FaFilePdf />
//                     );
//                 }

//                 if (
//                     [
//                         ".doc",
//                         ".docx",
//                     ].includes(
//                         extension
//                     ) ||
//                     contentType.includes(
//                         "word"
//                     )
//                 ) {
//                     return (
//                         <FaFileWord />
//                     );
//                 }

//                 if (
//                     [
//                         ".xls",
//                         ".xlsx",
//                         ".csv",
//                     ].includes(
//                         extension
//                     ) ||
//                     contentType.includes(
//                         "excel"
//                     ) ||
//                     contentType.includes(
//                         "spreadsheet"
//                     )
//                 ) {
//                     return (
//                         <FaFileExcel />
//                     );
//                 }

//                 if (
//                     [
//                         ".ppt",
//                         ".pptx",
//                     ].includes(
//                         extension
//                     ) ||
//                     contentType.includes(
//                         "powerpoint"
//                     ) ||
//                     contentType.includes(
//                         "presentation"
//                     )
//                 ) {
//                     return (
//                         <FaFilePowerpoint />
//                     );
//                 }

//                 if (
//                     extension ===
//                     ".txt"
//                 ) {
//                     return (
//                         <FaFileAlt />
//                     );
//                 }

//                 return <FaFile />;
//             },
//             []
//         );

//     const getResourceDownloadUrl =
//         useCallback(
//             (resource) => {
//                 if (
//                     !resource?.id ||
//                     !courseId
//                 ) {
//                     return "";
//                 }

//                 return (
//                     resource?.download_url ||
//                     resource?.downloadUrl ||
//                     `api/users/subjects/${courseId}/resources/${resource.id}/download/`
//                 );
//             },
//             [courseId]
//         );

//     const downloadResource =
//         useCallback(
//             async (resource) => {
//                 const downloadUrl =
//                     getResourceDownloadUrl(
//                         resource
//                     );

//                 if (
//                     !downloadUrl ||
//                     !resource?.id
//                 ) {
//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Download Unavailable",
//                         text:
//                             "This learning resource does not have a valid download link.",
//                         confirmButtonColor:
//                             "#062A4B",
//                     });

//                     return;
//                 }

//                 setDownloadingResourceId(
//                     resource.id
//                 );

//                 try {
//                     const response =
//                         await api.get(
//                             downloadUrl,
//                             {
//                                 responseType:
//                                     "blob",
//                             }
//                         );

//                     const responseType =
//                         response
//                             ?.headers?.[
//                             "content-type"
//                         ] ||
//                         resource?.content_type ||
//                         "application/octet-stream";

//                     const blob =
//                         new Blob(
//                             [
//                                 response.data,
//                             ],
//                             {
//                                 type: responseType,
//                             }
//                         );

//                     const blobUrl =
//                         window.URL.createObjectURL(
//                             blob
//                         );

//                     const anchor =
//                         document.createElement(
//                             "a"
//                         );

//                     const filename =
//                         resource?.original_filename ||
//                         resource?.originalFilename ||
//                         resource?.title ||
//                         "learning-resource";

//                     anchor.href =
//                         blobUrl;

//                     anchor.download =
//                         filename;

//                     document.body.appendChild(
//                         anchor
//                     );

//                     anchor.click();

//                     anchor.remove();

//                     window.setTimeout(
//                         () => {
//                             window.URL.revokeObjectURL(
//                                 blobUrl
//                             );
//                         },
//                         1000
//                     );
//                 } catch (error) {
//                     console.error(
//                         "Unable to download resource:",
//                         error
//                     );

//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Download Failed",
//                         text: getErrorMessage(
//                             error,
//                             "The learning resource could not be downloaded."
//                         ),
//                         confirmButtonColor:
//                             "#062A4B",
//                     });
//                 } finally {
//                     if (
//                         mountedRef.current
//                     ) {
//                         setDownloadingResourceId(
//                             null
//                         );
//                     }
//                 }
//             },
//             [
//                 getResourceDownloadUrl,
//                 getErrorMessage,
//             ]
//         );

//     const tabs = useMemo(
//         () => [
//             {
//                 key: "overview",
//                 icon: (
//                     <FaBookOpen />
//                 ),
//                 label: "Overview",
//             },
//             {
//                 key: "qa",
//                 icon: (
//                     <FaQuestionCircle />
//                 ),
//                 label: "Q&A",
//             },
//             {
//                 key: "notes",
//                 icon: (
//                     <FaStickyNote />
//                 ),
//                 label: "Notes",
//             },
//             {
//                 key: "announcements",
//                 icon: (
//                     <FaBullhorn />
//                 ),
//                 label: "Announcements",
//             },
//             {
//                 key: "reviews",
//                 icon: <FaStar />,
//                 label: "Reviews",
//             },
//             {
//                 key: "tools",
//                 icon: <FaTools />,
//                 label: "Learning Tools",
//             },
//         ],
//         []
//     );

//     if (!courseId) {
//         return (
//             <>
//                 <style>
//                     {
//                         CLASSROOM_STYLES
//                     }
//                 </style>

//                 <div className="cayep-classroom-empty">
//                     <div className="cayep-classroom-empty-icon">
//                         <FaBookOpen />
//                     </div>

//                     <h3>
//                         Classroom
//                         Unavailable
//                     </h3>

//                     <p>
//                         Select a course to
//                         access the learning
//                         environment.
//                     </p>
//                 </div>
//             </>
//         );
//     }

//     return (
//         <>
//             <style>
//                 {
//                     CLASSROOM_STYLES
//                 }
//             </style>

//             <div className="cayep-classroom-environment">
//                 <div className="cayep-classroom-header">
//                     <div className="cayep-classroom-header-main">
//                         <div className="cayep-classroom-header-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div className="cayep-classroom-header-copy">
//                             <span className="cayep-classroom-eyebrow">
//                                 {isManagement
//                                     ? "FACILITATOR VIEW"
//                                     : "LEARNER VIEW"}
//                             </span>

//                             <h2>
//                                 {course?.name ||
//                                     course?.title ||
//                                     "Course Classroom"}
//                             </h2>

//                             <div className="cayep-classroom-video-context">
//                                 <FaBookOpen />

//                                 <span>
//                                     {
//                                         videoContextLabel
//                                     }
//                                 </span>
//                             </div>
//                         </div>
//                     </div>

//                     <div className="cayep-classroom-status">
//                         <span className="cayep-classroom-status-dot" />

//                         <span>
//                             {isSectionVideo
//                                 ? "Section Classroom"
//                                 : "Course Classroom"}
//                         </span>
//                     </div>
//                 </div>

//                 {isPending && (
//                     <div className="cayep-classroom-approval-notice">
//                         <div className="cayep-classroom-approval-icon">
//                             <FaLock />
//                         </div>

//                         <div>
//                             <strong>
//                                 Course Access
//                                 Pending
//                             </strong>

//                             <p>
//                                 This course is
//                                 awaiting
//                                 approval before
//                                 classroom
//                                 content can be
//                                 accessed.
//                             </p>
//                         </div>
//                     </div>
//                 )}

//                 {!isPending && (
//                     <>
//                         <div className="cayep-classroom-tabs">
//                             {tabs.map(
//                                 (tab) => (
//                                     <button
//                                         key={
//                                             tab.key
//                                         }
//                                         type="button"
//                                         className={
//                                             activeTab ===
//                                             tab.key
//                                                 ? "active"
//                                                 : ""
//                                         }
//                                         onClick={() =>
//                                             setActiveTab(
//                                                 tab.key
//                                             )
//                                         }
//                                     >
//                                         <span className="cayep-classroom-tab-icon">
//                                             {
//                                                 tab.icon
//                                             }
//                                         </span>

//                                         <span className="cayep-classroom-tab-label">
//                                             {
//                                                 tab.label
//                                             }
//                                         </span>

//                                         {tab.key ===
//                                             "qa" &&
//                                             classroom.questions
//                                                 .length >
//                                                 0 && (
//                                                 <span className="cayep-classroom-tab-count">
//                                                     {
//                                                         classroom
//                                                             .questions
//                                                             .length
//                                                     }
//                                                 </span>
//                                             )}

//                                         {tab.key ===
//                                             "tools" &&
//                                             classroom.resources
//                                                 .length >
//                                                 0 && (
//                                                 <span className="cayep-classroom-tab-count">
//                                                     {
//                                                         classroom
//                                                             .resources
//                                                             .length
//                                                     }
//                                                 </span>
//                                             )}
//                                     </button>
//                                 )
//                             )}
//                         </div>

//                         <div className="cayep-classroom-panel">
//                             {loading ? (
//                                 <div className="cayep-classroom-loading">
//                                     <div className="cayep-classroom-spinner" />

//                                     <span>
//                                         Loading
//                                         classroom...
//                                     </span>
//                                 </div>
//                             ) : (
//                                 <>
//                                     {activeTab ===
//                                         "overview" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaBookOpen />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         COURSE
//                                                         CONTENT
//                                                     </span>

//                                                     <h3>
//                                                         Overview
//                                                     </h3>

//                                                     <p>
//                                                         Learn
//                                                         more about
//                                                         this video
//                                                         and its
//                                                         training
//                                                         content.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .overview
//                                                 ?.content ? (
//                                                 <div className="cayep-classroom-rich-content">
//                                                     {String(
//                                                         classroom
//                                                             .overview
//                                                             .content
//                                                     )
//                                                         .split(
//                                                             /\r?\n/
//                                                         )
//                                                         .map(
//                                                             (
//                                                                 paragraph,
//                                                                 index
//                                                             ) =>
//                                                                 paragraph.trim() ? (
//                                                                     <p
//                                                                         key={
//                                                                             index
//                                                                         }
//                                                                     >
//                                                                         {
//                                                                             paragraph
//                                                                         }
//                                                                     </p>
//                                                                 ) : null
//                                                         )}
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaLightbulb />

//                                                     <h4>
//                                                         No
//                                                         Overview
//                                                         Yet
//                                                     </h4>

//                                                     <p>
//                                                         The
//                                                         facilitator
//                                                         has not
//                                                         added an
//                                                         overview
//                                                         for this
//                                                         video yet.
//                                                     </p>
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "qa" && (
//                                         <QuestionAndAnswer
//                                             course={
//                                                 course
//                                             }
//                                             classroom={
//                                                 classroom
//                                             }
//                                             setClassroom={
//                                                 setClassroom
//                                             }
//                                             isStudent={
//                                                 isStudent
//                                             }
//                                             isManagement={
//                                                 isManagement
//                                             }
//                                             videoType={
//                                                 videoType
//                                             }
//                                             videoSection={
//                                                 videoSection
//                                             }
//                                             videoContextLabel={
//                                                 videoContextLabel
//                                             }
//                                             loadClassroom={
//                                                 loadClassroom
//                                             }
//                                         />
//                                     )}

//                                     {activeTab ===
//                                         "notes" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaStickyNote />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         PRIVATE
//                                                         LEARNING
//                                                     </span>

//                                                     <h3>
//                                                         My Notes
//                                                     </h3>

//                                                     <p>
//                                                         Your
//                                                         notes are
//                                                         private
//                                                         and saved
//                                                         specifically
//                                                         for this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {isStudent ? (
//                                                 <form
//                                                     className="cayep-classroom-notes-form"
//                                                     onSubmit={
//                                                         saveNote
//                                                     }
//                                                 >
//                                                     <div className="cayep-classroom-notes-toolbar">
//                                                         <span>
//                                                             <FaPen />
//                                                             Personal
//                                                             notes
//                                                         </span>

//                                                         {noteSaved && (
//                                                             <span className="cayep-classroom-note-saved">
//                                                                 <FaCheckCircle />
//                                                                 Saved
//                                                             </span>
//                                                         )}
//                                                     </div>

//                                                     <textarea
//                                                         value={
//                                                             noteContent
//                                                         }
//                                                         onChange={(
//                                                             event
//                                                         ) =>
//                                                             setNoteContent(
//                                                                 event
//                                                                     .target
//                                                                     .value
//                                                             )
//                                                         }
//                                                         placeholder="Write your private notes for this video..."
//                                                         rows={
//                                                             12
//                                                         }
//                                                         disabled={
//                                                             savingNote
//                                                         }
//                                                     />

//                                                     <div className="cayep-classroom-notes-footer">
//                                                         <span>
//                                                             <FaLock />
//                                                             Only you
//                                                             can view
//                                                             these
//                                                             notes.
//                                                         </span>

//                                                         <button
//                                                             type="submit"
//                                                             disabled={
//                                                                 savingNote
//                                                             }
//                                                         >
//                                                             <FaSave />

//                                                             {savingNote
//                                                                 ? "Saving..."
//                                                                 : "Save Notes"}
//                                                         </button>
//                                                     </div>
//                                                 </form>
//                                             ) : (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaLock />

//                                                     <h4>
//                                                         Student
//                                                         Notes
//                                                     </h4>

//                                                     <p>
//                                                         Notes are
//                                                         a private
//                                                         student
//                                                         learning
//                                                         feature.
//                                                     </p>
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "announcements" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaBullhorn />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         UPDATES
//                                                     </span>

//                                                     <h3>
//                                                         Announcements
//                                                     </h3>

//                                                     <p>
//                                                         Important
//                                                         information
//                                                         from the
//                                                         facilitator
//                                                         for this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .announcements
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaBullhorn />

//                                                     <h4>
//                                                         No
//                                                         Announcements
//                                                     </h4>

//                                                     <p>
//                                                         There are
//                                                         no
//                                                         announcements
//                                                         for this
//                                                         video at
//                                                         the moment.
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-announcement-list">
//                                                     {classroom.announcements.map(
//                                                         (
//                                                             announcement,
//                                                             index
//                                                         ) => (
//                                                             <article
//                                                                 className="cayep-classroom-announcement-card"
//                                                                 key={
//                                                                     announcement?.id ||
//                                                                     index
//                                                                 }
//                                                             >
//                                                                 <div className="cayep-classroom-announcement-icon">
//                                                                     <FaBullhorn />
//                                                                 </div>

//                                                                 <div className="cayep-classroom-announcement-body">
//                                                                     <div className="cayep-classroom-announcement-meta">
//                                                                         <span>
//                                                                             {formatDate(
//                                                                                 announcement?.created_at ||
//                                                                                     announcement?.createdAt
//                                                                             )}
//                                                                         </span>
//                                                                     </div>

//                                                                     <h4>
//                                                                         {
//                                                                             announcement?.title
//                                                                         }
//                                                                     </h4>

//                                                                     <p>
//                                                                         {
//                                                                             announcement?.content
//                                                                         }
//                                                                     </p>
//                                                                 </div>
//                                                             </article>
//                                                         )
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "reviews" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaStar />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         FACILITATOR
//                                                         FEEDBACK
//                                                     </span>

//                                                     <h3>
//                                                         Reviews
//                                                     </h3>

//                                                     <p>
//                                                         Guidance
//                                                         and
//                                                         feedback
//                                                         associated
//                                                         with this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .reviews
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaStar />

//                                                     <h4>
//                                                         No Reviews
//                                                         Yet
//                                                     </h4>

//                                                     <p>
//                                                         No
//                                                         facilitator
//                                                         review has
//                                                         been added
//                                                         for this
//                                                         video yet.
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-review-list">
//                                                     {classroom.reviews.map(
//                                                         (
//                                                             review,
//                                                             index
//                                                         ) => (
//                                                             <article
//                                                                 className="cayep-classroom-review-card"
//                                                                 key={
//                                                                     review?.id ||
//                                                                     index
//                                                                 }
//                                                             >
//                                                                 <div className="cayep-classroom-review-header">
//                                                                     <div className="cayep-classroom-review-icon">
//                                                                         <FaStar />
//                                                                     </div>

//                                                                     <div>
//                                                                         <strong>
//                                                                             Facilitator
//                                                                             Review
//                                                                         </strong>

//                                                                         <span>
//                                                                             {formatDate(
//                                                                                 review?.created_at ||
//                                                                                     review?.createdAt
//                                                                             )}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>

//                                                                 <p>
//                                                                     {
//                                                                         review?.content
//                                                                     }
//                                                                 </p>
//                                                             </article>
//                                                         )
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "tools" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaTools />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         COURSE
//                                                         MATERIALS
//                                                     </span>

//                                                     <h3>
//                                                         Learning
//                                                         Tools
//                                                     </h3>

//                                                     <p>
//                                                         Download
//                                                         learning
//                                                         materials
//                                                         provided
//                                                         specifically
//                                                         for this
//                                                         video.
//                                                     </p>

//                                                     <div className="cayep-classroom-context-pill">
//                                                         <FaBookOpen />

//                                                         <span>
//                                                             {
//                                                                 videoContextLabel
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .resources
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaTools />

//                                                     <h4>
//                                                         No Learning
//                                                         Resources
//                                                     </h4>

//                                                     <p>
//                                                         {isSectionVideo
//                                                             ? "No files have been uploaded for this section yet."
//                                                             : "No files have been uploaded for the Introduction Video yet."}
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-resource-list">
//                                                     {classroom.resources.map(
//                                                         (
//                                                             resource,
//                                                             index
//                                                         ) => {
//                                                             const filename =
//                                                                 resource?.original_filename ||
//                                                                 resource?.originalFilename ||
//                                                                 resource?.title ||
//                                                                 "Learning Resource";

//                                                             const extension =
//                                                                 resource?.file_extension ||
//                                                                 resource?.extension ||
//                                                                 "";

//                                                             const fileSize =
//                                                                 formatFileSize(
//                                                                     resource?.file_size ??
//                                                                         resource?.fileSize
//                                                                 );

//                                                             const isDownloading =
//                                                                 String(
//                                                                     downloadingResourceId
//                                                                 ) ===
//                                                                 String(
//                                                                     resource?.id
//                                                                 );

//                                                             return (
//                                                                 <article
//                                                                     className="cayep-classroom-resource-card"
//                                                                     key={
//                                                                         resource?.id ||
//                                                                         index
//                                                                     }
//                                                                 >
//                                                                     <div className="cayep-classroom-resource-icon">
//                                                                         {getFileIcon(
//                                                                             resource
//                                                                         )}
//                                                                     </div>

//                                                                     <div className="cayep-classroom-resource-info">
//                                                                         <div className="cayep-classroom-resource-top">
//                                                                             <h4>
//                                                                                 {
//                                                                                     resource?.title
//                                                                                 }
//                                                                             </h4>

//                                                                             {extension && (
//                                                                                 <span className="cayep-classroom-resource-extension">
//                                                                                     {String(
//                                                                                         extension
//                                                                                     )
//                                                                                         .replace(
//                                                                                             ".",
//                                                                                             ""
//                                                                                         )
//                                                                                         .toUpperCase()}
//                                                                                 </span>
//                                                                             )}
//                                                                         </div>

//                                                                         <p className="cayep-classroom-resource-filename">
//                                                                             {
//                                                                                 filename
//                                                                             }
//                                                                         </p>

//                                                                         {resource?.description && (
//                                                                             <p className="cayep-classroom-resource-description">
//                                                                                 {
//                                                                                     resource.description
//                                                                                 }
//                                                                             </p>
//                                                                         )}

//                                                                         <div className="cayep-classroom-resource-meta">
//                                                                             <span>
//                                                                                 {
//                                                                                     fileSize
//                                                                                 }
//                                                                             </span>

//                                                                             {resource?.created_at && (
//                                                                                 <>
//                                                                                     <span className="cayep-resource-meta-dot">
//                                                                                         •
//                                                                                     </span>

//                                                                                     <span>
//                                                                                         Uploaded{" "}
//                                                                                         {formatDate(
//                                                                                             resource.created_at
//                                                                                         )}
//                                                                                     </span>
//                                                                                 </>
//                                                                             )}
//                                                                         </div>
//                                                                     </div>

//                                                                     <button
//                                                                         type="button"
//                                                                         className="cayep-classroom-resource-download"
//                                                                         onClick={() =>
//                                                                             downloadResource(
//                                                                                 resource
//                                                                             )
//                                                                         }
//                                                                         disabled={
//                                                                             isDownloading
//                                                                         }
//                                                                     >
//                                                                         <FaDownload />

//                                                                         <span>
//                                                                             {isDownloading
//                                                                                 ? "Downloading..."
//                                                                                 : "Download"}
//                                                                         </span>
//                                                                     </button>
//                                                                 </article>
//                                                             );
//                                                         }
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}
//                                 </>
//                             )}
//                         </div>
//                     </>
//                 )}
//             </div>
//         </>
//     );
// };

// export default ClassRoomEnvironment;






// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useRef,
//     useState,
// } from "react";

// import {
//     FaBookOpen,
//     FaBullhorn,
//     FaChalkboardTeacher,
//     FaCheckCircle,
//     FaComment,
//     FaDownload,
//     FaFile,
//     FaFileAlt,
//     FaFileExcel,
//     FaFilePdf,
//     FaFilePowerpoint,
//     FaFileWord,
//     FaGraduationCap,
//     FaLightbulb,
//     FaLock,
//     FaPaperPlane,
//     FaPen,
//     FaQuestionCircle,
//     FaReply,
//     FaSave,
//     FaStar,
//     FaStickyNote,
//     FaTools,
//     FaUser,
//     FaUsers,
// } from "react-icons/fa";

// import Swal from "sweetalert2";

// import api from "../api/api";
// import defaultAvatar from "./learningenvironment.jpg";

// const EMPTY_CLASSROOM = {
//     overview: null,
//     questions: [],
//     notes: null,
//     announcements: [],
//     reviews: [],
//     resources: [],
// };

// const MANAGEMENT_ROLES = new Set([
//     "teacher",
//     "facilitator",
//     "instructor",
//     "admin",
//     "administrator",
//     "principal",
//     "proprietor",
// ]);

// const ClassRoomEnvironment = ({
//     course,
//     videoType = "introduction",
//     videoSection = null,
// }) => {
//     const [activeTab, setActiveTab] = useState("overview");

//     const [classroom, setClassroom] =
//         useState(EMPTY_CLASSROOM);

//     const [loading, setLoading] = useState(true);

//     const [questionText, setQuestionText] =
//         useState("");

//     const [replyTexts, setReplyTexts] =
//         useState({});

//     const [noteContent, setNoteContent] =
//         useState("");

//     const [noteSaved, setNoteSaved] =
//         useState(false);

//     const [expandedQuestions, setExpandedQuestions] =
//         useState({});

//     const [savingQuestion, setSavingQuestion] =
//         useState(false);

//     const [savingNote, setSavingNote] =
//         useState(false);

//     const [savingReplyId, setSavingReplyId] =
//         useState(null);

//     const [downloadingResourceId, setDownloadingResourceId] =
//         useState(null);

//     const [role, setRole] = useState("");

//     const mountedRef = useRef(true);

//     const classroomRequestRef = useRef(0);

//     const noteSavedTimerRef = useRef(null);

//     const courseId = course?.id;

//     const isStudent = role === "student";

//     const isManagement = MANAGEMENT_ROLES.has(role);

//     const isSectionVideo =
//         videoType === "section" &&
//         Boolean(
//             videoSection?.id ||
//                 videoSection?.section_id ||
//                 videoSection?.sectionId
//         );

//     const sectionId = useMemo(() => {
//         if (!isSectionVideo) {
//             return null;
//         }

//         return (
//             videoSection?.id ||
//             videoSection?.section_id ||
//             videoSection?.sectionId ||
//             null
//         );
//     }, [isSectionVideo, videoSection]);

//     const learningStatus = useMemo(() => {
//         const value =
//             course?.learning_status ??
//             course?.learningStatus ??
//             course?.approval_status ??
//             course?.approvalStatus ??
//             "";

//         return String(value)
//             .trim()
//             .toLowerCase();
//     }, [course]);

//     const hasExplicitLearningStatus =
//         Boolean(learningStatus);

//     const isApproved =
//         !hasExplicitLearningStatus ||
//         learningStatus === "approved" ||
//         learningStatus === "active" ||
//         learningStatus === "published";

//     const isPending =
//         Boolean(courseId) &&
//         hasExplicitLearningStatus &&
//         !isApproved;

//     const videoContextLabel = useMemo(() => {
//         if (!isSectionVideo) {
//             return "Introduction Video";
//         }

//         const number =
//             videoSection?.section_number ||
//             videoSection?.sectionNumber ||
//             "";

//         const title =
//             videoSection?.title ||
//             videoSection?.name ||
//             "Course Section";

//         if (number) {
//             return `Section ${number} • ${title}`;
//         }

//         return title;
//     }, [
//         isSectionVideo,
//         videoSection,
//     ]);

//     const classroomPath = useMemo(() => {
//         if (!courseId) {
//             return "";
//         }

//         if (isSectionVideo && sectionId) {
//             return `api/users/subjects/${courseId}/video-sections/${sectionId}/classroom/`;
//         }

//         return `api/users/subjects/${courseId}/classroom/`;
//     }, [
//         courseId,
//         isSectionVideo,
//         sectionId,
//     ]);

//     const questionsPath = useMemo(() => {
//         if (!courseId) {
//             return "";
//         }

//         if (isSectionVideo && sectionId) {
//             return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/`;
//         }

//         return `api/users/subjects/${courseId}/questions/`;
//     }, [
//         courseId,
//         isSectionVideo,
//         sectionId,
//     ]);

//     const notesPath = useMemo(() => {
//         if (!courseId) {
//             return "";
//         }

//         if (isSectionVideo && sectionId) {
//             return `api/users/subjects/${courseId}/video-sections/${sectionId}/notes/`;
//         }

//         return `api/users/subjects/${courseId}/notes/`;
//     }, [
//         courseId,
//         isSectionVideo,
//         sectionId,
//     ]);

//     const replyPath = useCallback(
//         (questionId) => {
//             if (!courseId || !questionId) {
//                 return "";
//             }

//             if (isSectionVideo && sectionId) {
//                 return `api/users/subjects/${courseId}/video-sections/${sectionId}/questions/${questionId}/reply/`;
//             }

//             return `api/users/subjects/${courseId}/questions/${questionId}/reply/`;
//         },
//         [
//             courseId,
//             isSectionVideo,
//             sectionId,
//         ]
//     );

//     const getArray = useCallback((value) => {
//         if (Array.isArray(value)) {
//             return value;
//         }

//         if (Array.isArray(value?.results)) {
//             return value.results;
//         }

//         if (Array.isArray(value?.data)) {
//             return value.data;
//         }

//         return [];
//     }, []);

//     const normalizeClassroomResponse = useCallback(
//         (data) => {
//             const source =
//                 data?.data &&
//                 typeof data.data === "object" &&
//                 !Array.isArray(data.data)
//                     ? data.data
//                     : data || {};

//             return {
//                 overview:
//                     source?.overview ||
//                     source?.course_overview ||
//                     source?.video_section_overview ||
//                     null,

//                 questions: getArray(
//                     source?.questions ||
//                         source?.qa ||
//                         source?.question_and_answers
//                 ),

//                 notes:
//                     source?.notes ||
//                     source?.note ||
//                     null,

//                 announcements: getArray(
//                     source?.announcements
//                 ),

//                 reviews: getArray(
//                     source?.reviews
//                 ),

//                 resources: getArray(
//                     source?.resources ||
//                         source?.learning_resources ||
//                         source?.course_resources
//                 ),
//             };
//         },
//         [getArray]
//     );

//     const readStoredRole = useCallback(() => {
//         try {
//             const storedUser =
//                 localStorage.getItem("user");

//             if (storedUser) {
//                 const parsedUser =
//                     JSON.parse(storedUser);

//                 const storedRole =
//                     parsedUser?.user_type ||
//                     parsedUser?.role ||
//                     parsedUser?.user_role ||
//                     "";

//                 if (storedRole) {
//                     return String(storedRole)
//                         .trim()
//                         .toLowerCase();
//                 }
//             }
//         } catch (error) {
//             console.warn(
//                 "Unable to read stored user:",
//                 error
//             );
//         }

//         return String(
//             localStorage.getItem("role") ||
//                 localStorage.getItem("user_role") ||
//                 localStorage.getItem("user_type") ||
//                 ""
//         )
//             .trim()
//             .toLowerCase();
//     }, []);

//     useEffect(() => {
//         mountedRef.current = true;

//         setRole(readStoredRole());

//         const refreshRole = () => {
//             if (mountedRef.current) {
//                 setRole(readStoredRole());
//             }
//         };

//         window.addEventListener(
//             "storage",
//             refreshRole
//         );

//         window.addEventListener(
//             "user-logged-in",
//             refreshRole
//         );

//         window.addEventListener(
//             "force-logout",
//             refreshRole
//         );

//         return () => {
//             mountedRef.current = false;

//             window.removeEventListener(
//                 "storage",
//                 refreshRole
//             );

//             window.removeEventListener(
//                 "user-logged-in",
//                 refreshRole
//             );

//             window.removeEventListener(
//                 "force-logout",
//                 refreshRole
//             );

//             if (noteSavedTimerRef.current) {
//                 clearTimeout(
//                     noteSavedTimerRef.current
//                 );
//             }
//         };
//     }, [readStoredRole]);

//     const formatDate = useCallback((value) => {
//         if (!value) {
//             return "";
//         }

//         const date = new Date(value);

//         if (Number.isNaN(date.getTime())) {
//             return "";
//         }

//         try {
//             return new Intl.DateTimeFormat(
//                 "en-US",
//                 {
//                     dateStyle: "medium",
//                     timeStyle: "short",
//                 }
//             ).format(date);
//         } catch {
//             return "";
//         }
//     }, []);

//     const formatFileSize = useCallback((bytes) => {
//         const size = Number(bytes);

//         if (!Number.isFinite(size) || size <= 0) {
//             return "File";
//         }

//         if (size < 1024) {
//             return `${size} B`;
//         }

//         if (size < 1024 * 1024) {
//             return `${(
//                 size / 1024
//             ).toFixed(1)} KB`;
//         }

//         if (size < 1024 * 1024 * 1024) {
//             return `${(
//                 size /
//                 (1024 * 1024)
//             ).toFixed(1)} MB`;
//         }

//         return `${(
//             size /
//             (1024 * 1024 * 1024)
//         ).toFixed(1)} GB`;
//     }, []);

//     const getErrorMessage = useCallback(
//         (error, fallback) => {
//             const data =
//                 error?.response?.data;

//             if (typeof data === "string") {
//                 return data;
//             }

//             if (data?.detail) {
//                 return data.detail;
//             }

//             if (data?.message) {
//                 return data.message;
//             }

//             if (data?.error) {
//                 return data.error;
//             }

//             if (data?.question) {
//                 return Array.isArray(
//                     data.question
//                 )
//                     ? data.question.join(" ")
//                     : data.question;
//             }

//             if (data?.reply) {
//                 return Array.isArray(
//                     data.reply
//                 )
//                     ? data.reply.join(" ")
//                     : data.reply;
//             }

//             if (data?.content) {
//                 return Array.isArray(
//                     data.content
//                 )
//                     ? data.content.join(" ")
//                     : data.content;
//             }

//             return fallback;
//         },
//         []
//     );

//     const loadClassroom = useCallback(
//         async (showLoader = true) => {
//             if (!courseId || !classroomPath) {
//                 if (mountedRef.current) {
//                     setClassroom(
//                         EMPTY_CLASSROOM
//                     );
//                     setLoading(false);
//                 }

//                 return;
//             }

//             const requestId =
//                 ++classroomRequestRef.current;

//             if (
//                 showLoader &&
//                 mountedRef.current
//             ) {
//                 setLoading(true);
//             }

//             try {
//                 const response =
//                     await api.get(
//                         classroomPath
//                     );

//                 if (
//                     !mountedRef.current ||
//                     requestId !==
//                         classroomRequestRef.current
//                 ) {
//                     return;
//                 }

//                 const normalized =
//                     normalizeClassroomResponse(
//                         response?.data || {}
//                     );

//                 setClassroom(normalized);

//                 setNoteContent(
//                     normalized.notes?.content ||
//                         ""
//                 );

//                 setNoteSaved(false);
//             } catch (error) {
//                 if (
//                     !mountedRef.current ||
//                     requestId !==
//                         classroomRequestRef.current
//                 ) {
//                     return;
//                 }

//                 console.error(
//                     "Unable to load classroom:",
//                     error
//                 );

//                 setClassroom(
//                     EMPTY_CLASSROOM
//                 );

//                 setNoteContent("");

//                 const status =
//                     error?.response?.status;

//                 if (
//                     status !== 401 &&
//                     status !== 403 &&
//                     status !== 404
//                 ) {
//                     Swal.fire({
//                         icon: "error",
//                         title:
//                             "Unable to Load Classroom",
//                         text: getErrorMessage(
//                             error,
//                             "The classroom information could not be loaded."
//                         ),
//                         confirmButtonColor:
//                             "#062A4B",
//                     });
//                 }
//             } finally {
//                 if (
//                     mountedRef.current &&
//                     requestId ===
//                         classroomRequestRef.current &&
//                     showLoader
//                 ) {
//                     setLoading(false);
//                 }
//             }
//         },
//         [
//             courseId,
//             classroomPath,
//             normalizeClassroomResponse,
//             getErrorMessage,
//         ]
//     );

//     useEffect(() => {
//         setActiveTab("overview");
//         setQuestionText("");
//         setReplyTexts({});
//         setExpandedQuestions({});
//         setNoteContent("");
//         setNoteSaved(false);
//         setSavingQuestion(false);
//         setSavingNote(false);
//         setSavingReplyId(null);
//     }, [
//         courseId,
//         videoType,
//         sectionId,
//     ]);

//     useEffect(() => {
//         loadClassroom(true);
//     }, [loadClassroom]);

//     const getFileIcon = useCallback(
//         (resource) => {
//             const extension =
//                 String(
//                     resource?.file_extension ||
//                         resource?.extension ||
//                         ""
//                 ).toLowerCase();

//             const contentType =
//                 String(
//                     resource?.content_type ||
//                         resource?.contentType ||
//                         ""
//                 ).toLowerCase();

//             if (
//                 extension === ".pdf" ||
//                 contentType.includes("pdf")
//             ) {
//                 return <FaFilePdf />;
//             }

//             if (
//                 [".doc", ".docx"].includes(
//                     extension
//                 ) ||
//                 contentType.includes("word")
//             ) {
//                 return <FaFileWord />;
//             }

//             if (
//                 [
//                     ".xls",
//                     ".xlsx",
//                     ".csv",
//                 ].includes(extension) ||
//                 contentType.includes(
//                     "excel"
//                 ) ||
//                 contentType.includes(
//                     "spreadsheet"
//                 )
//             ) {
//                 return <FaFileExcel />;
//             }

//             if (
//                 [
//                     ".ppt",
//                     ".pptx",
//                 ].includes(extension) ||
//                 contentType.includes(
//                     "powerpoint"
//                 ) ||
//                 contentType.includes(
//                     "presentation"
//                 )
//             ) {
//                 return <FaFilePowerpoint />;
//             }

//             if (extension === ".txt") {
//                 return <FaFileAlt />;
//             }

//             return <FaFile />;
//         },
//         []
//     );

//     const getResourceDownloadUrl =
//         useCallback(
//             (resource) => {
//                 if (
//                     !resource?.id ||
//                     !courseId
//                 ) {
//                     return "";
//                 }

//                 return (
//                     resource?.download_url ||
//                     resource?.downloadUrl ||
//                     `api/users/subjects/${courseId}/resources/${resource.id}/download/`
//                 );
//             },
//             [courseId]
//         );

//     const downloadResource = useCallback(
//         async (resource) => {
//             const downloadUrl =
//                 getResourceDownloadUrl(
//                     resource
//                 );

//             if (
//                 !downloadUrl ||
//                 !resource?.id
//             ) {
//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         "Download Unavailable",
//                     text:
//                         "This learning resource does not have a valid download link.",
//                     confirmButtonColor:
//                         "#062A4B",
//                 });

//                 return;
//             }

//             setDownloadingResourceId(
//                 resource.id
//             );

//             try {
//                 const response =
//                     await api.get(
//                         downloadUrl,
//                         {
//                             responseType:
//                                 "blob",
//                         }
//                     );

//                 const responseType =
//                     response?.headers?.[
//                         "content-type"
//                     ] ||
//                     resource?.content_type ||
//                     "application/octet-stream";

//                 const blob = new Blob(
//                     [response.data],
//                     {
//                         type: responseType,
//                     }
//                 );

//                 const blobUrl =
//                     window.URL.createObjectURL(
//                         blob
//                     );

//                 const anchor =
//                     document.createElement(
//                         "a"
//                     );

//                 const filename =
//                     resource?.original_filename ||
//                     resource?.originalFilename ||
//                     resource?.title ||
//                     "learning-resource";

//                 anchor.href = blobUrl;
//                 anchor.download =
//                     filename;

//                 document.body.appendChild(
//                     anchor
//                 );

//                 anchor.click();

//                 anchor.remove();

//                 window.setTimeout(() => {
//                     window.URL.revokeObjectURL(
//                         blobUrl
//                     );
//                 }, 1000);
//             } catch (error) {
//                 console.error(
//                     "Unable to download resource:",
//                     error
//                 );

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         "Download Failed",
//                     text: getErrorMessage(
//                         error,
//                         "The learning resource could not be downloaded."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setDownloadingResourceId(
//                         null
//                     );
//                 }
//             }
//         },
//         [
//             getResourceDownloadUrl,
//             getErrorMessage,
//         ]
//     );

//     const submitQuestion = useCallback(
//         async (event) => {
//             event.preventDefault();

//             const text =
//                 questionText.trim();

//             if (
//                 !isStudent ||
//                 !text ||
//                 savingQuestion ||
//                 !questionsPath
//             ) {
//                 return;
//             }

//             setSavingQuestion(true);

//             try {
//                 const response =
//                     await api.post(
//                         questionsPath,
//                         {
//                             question: text,
//                         }
//                     );

//                 const createdQuestion =
//                     response?.data;

//                 if (
//                     createdQuestion &&
//                     typeof createdQuestion ===
//                         "object"
//                 ) {
//                     setClassroom(
//                         (previous) => ({
//                             ...previous,
//                             questions: [
//                                 createdQuestion,
//                                 ...previous.questions,
//                             ],
//                         })
//                     );
//                 }

//                 setQuestionText("");

//                 setActiveTab("qa");

//                 await loadClassroom(false);

//                 Swal.fire({
//                     icon: "success",
//                     title:
//                         "Question Posted",
//                     text:
//                         "Your question has been added to the learning community.",
//                     timer: 1500,
//                     showConfirmButton:
//                         false,
//                 });
//             } catch (error) {
//                 console.error(
//                     "Unable to submit question:",
//                     error
//                 );

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         "Question Not Posted",
//                     text: getErrorMessage(
//                         error,
//                         "Your question could not be posted."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setSavingQuestion(false);
//                 }
//             }
//         },
//         [
//             questionText,
//             isStudent,
//             savingQuestion,
//             questionsPath,
//             loadClassroom,
//             getErrorMessage,
//         ]
//     );

//     const submitReply = useCallback(
//         async (event, questionId) => {
//             event.preventDefault();

//             const text = String(
//                 replyTexts[questionId] ||
//                     ""
//             ).trim();

//             if (
//                 !questionId ||
//                 !text ||
//                 savingReplyId !== null
//             ) {
//                 return;
//             }

//             if (
//                 !isStudent &&
//                 !isManagement
//             ) {
//                 return;
//             }

//             const endpoint =
//                 replyPath(questionId);

//             if (!endpoint) {
//                 return;
//             }

//             setSavingReplyId(
//                 questionId
//             );

//             try {
//                 const response =
//                     await api.post(
//                         endpoint,
//                         {
//                             reply: text,
//                         }
//                     );

//                 const createdReply =
//                     response?.data;

//                 if (
//                     createdReply &&
//                     typeof createdReply ===
//                         "object"
//                 ) {
//                     setClassroom(
//                         (previous) => ({
//                             ...previous,
//                             questions:
//                                 previous.questions.map(
//                                     (
//                                         question
//                                     ) => {
//                                         if (
//                                             String(
//                                                 question?.id
//                                             ) !==
//                                             String(
//                                                 questionId
//                                             )
//                                         ) {
//                                             return question;
//                                         }

//                                         return {
//                                             ...question,
//                                             replies: [
//                                                 ...(Array.isArray(
//                                                     question?.replies
//                                                 )
//                                                     ? question.replies
//                                                     : []),
//                                                 createdReply,
//                                             ],
//                                         };
//                                     }
//                                 ),
//                         })
//                     );
//                 }

//                 setReplyTexts(
//                     (previous) => ({
//                         ...previous,
//                         [questionId]:
//                             "",
//                     })
//                 );

//                 setExpandedQuestions(
//                     (previous) => ({
//                         ...previous,
//                         [questionId]:
//                             true,
//                     })
//                 );

//                 await loadClassroom(
//                     false
//                 );

//                 Swal.fire({
//                     icon: "success",
//                     title:
//                         "Reply Posted",
//                     text:
//                         "Your response has been added to the discussion.",
//                     timer: 1200,
//                     showConfirmButton:
//                         false,
//                 });
//             } catch (error) {
//                 console.error(
//                     "Unable to submit reply:",
//                     error
//                 );

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         "Reply Not Posted",
//                     text: getErrorMessage(
//                         error,
//                         "Your reply could not be posted."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setSavingReplyId(null);
//                 }
//             }
//         },
//         [
//             replyTexts,
//             savingReplyId,
//             isStudent,
//             isManagement,
//             replyPath,
//             loadClassroom,
//             getErrorMessage,
//         ]
//     );

//     const saveNote = useCallback(
//         async (event) => {
//             event.preventDefault();

//             if (
//                 !isStudent ||
//                 !notesPath ||
//                 savingNote
//             ) {
//                 return;
//             }

//             setSavingNote(true);
//             setNoteSaved(false);

//             const content =
//                 noteContent;

//             try {
//                 const existingNote =
//                     classroom?.notes;

//                 let response;

//                 if (
//                     existingNote?.id
//                 ) {
//                     response =
//                         await api.patch(
//                             notesPath,
//                             {
//                                 content,
//                             }
//                         );
//                 } else {
//                     response =
//                         await api.post(
//                             notesPath,
//                             {
//                                 content,
//                             }
//                         );
//                 }

//                 const savedNote =
//                     response?.data;

//                 setClassroom(
//                     (previous) => ({
//                         ...previous,
//                         notes:
//                             savedNote || {
//                                 ...previous.notes,
//                                 content,
//                             },
//                     })
//                 );

//                 setNoteContent(
//                     savedNote?.content ??
//                         content
//                 );

//                 setNoteSaved(true);

//                 if (
//                     noteSavedTimerRef.current
//                 ) {
//                     clearTimeout(
//                         noteSavedTimerRef.current
//                     );
//                 }

//                 noteSavedTimerRef.current =
//                     window.setTimeout(
//                         () => {
//                             if (
//                                 mountedRef.current
//                             ) {
//                                 setNoteSaved(
//                                     false
//                                 );
//                             }
//                         },
//                         2500
//                     );
//             } catch (error) {
//                 console.error(
//                     "Unable to save note:",
//                     error
//                 );

//                 Swal.fire({
//                     icon: "error",
//                     title:
//                         "Note Not Saved",
//                     text: getErrorMessage(
//                         error,
//                         "Your private note could not be saved."
//                     ),
//                     confirmButtonColor:
//                         "#062A4B",
//                 });
//             } finally {
//                 if (mountedRef.current) {
//                     setSavingNote(false);
//                 }
//             }
//         },
//         [
//             isStudent,
//             notesPath,
//             savingNote,
//             noteContent,
//             classroom?.notes,
//             getErrorMessage,
//         ]
//     );

//     const toggleQuestion = useCallback(
//         (questionId) => {
//             if (!questionId) {
//                 return;
//             }

//             setExpandedQuestions(
//                 (previous) => ({
//                     ...previous,
//                     [questionId]:
//                         !previous[
//                             questionId
//                         ],
//                 })
//             );
//         },
//         []
//     );

//     const getQuestionAvatar =
//         useCallback((question) => {
//             return (
//                 question?.student_avatar ||
//                 question?.student?.avatar ||
//                 question?.student_image ||
//                 question?.student?.image ||
//                 defaultAvatar
//             );
//         }, []);

//     const getQuestionAuthor =
//         useCallback((question) => {
//             return (
//                 question?.student_name ||
//                 question?.student?.name ||
//                 question?.student?.username ||
//                 question?.student_username ||
//                 "Learner"
//             );
//         }, []);

//     const getReplyAuthor =
//         useCallback((reply) => {
//             return (
//                 reply?.author_name ||
//                 reply?.author?.name ||
//                 reply?.author?.username ||
//                 reply?.student_name ||
//                 reply?.student?.name ||
//                 "Learner"
//             );
//         }, []);

//     const getReplyAvatar =
//         useCallback((reply) => {
//             return (
//                 reply?.author_avatar ||
//                 reply?.author?.avatar ||
//                 reply?.author?.image ||
//                 reply?.student_avatar ||
//                 reply?.student?.avatar ||
//                 reply?.student?.image ||
//                 defaultAvatar
//             );
//         }, []);

//     const isReplyFromManagement =
//         useCallback((reply) => {
//             const replyRole =
//                 String(
//                     reply?.author_role ||
//                         reply?.author?.user_type ||
//                         reply?.author?.role ||
//                         reply?.author?.user_role ||
//                         ""
//                 )
//                     .trim()
//                     .toLowerCase();

//             if (
//                 MANAGEMENT_ROLES.has(
//                     replyRole
//                 )
//             ) {
//                 return true;
//             }

//             if (
//                 reply?.is_facilitator ===
//                     true ||
//                 reply?.is_teacher === true ||
//                 reply?.is_management ===
//                     true
//             ) {
//                 return true;
//             }

//             return false;
//         }, []);

//     const getQuestionReplies =
//         useCallback((question) => {
//             if (
//                 Array.isArray(
//                     question?.replies
//                 )
//             ) {
//                 return question.replies;
//             }

//             if (
//                 Array.isArray(
//                     question?.question_replies
//                 )
//             ) {
//                 return question.question_replies;
//             }

//             if (
//                 Array.isArray(
//                     question?.answers
//                 )
//             ) {
//                 return question.answers;
//             }

//             return [];
//         }, []);

//     const tabs = useMemo(
//         () => [
//             {
//                 key: "overview",
//                 icon: <FaBookOpen />,
//                 label: "Overview",
//             },
//             {
//                 key: "qa",
//                 icon: <FaQuestionCircle />,
//                 label: "Q&A",
//             },
//             {
//                 key: "notes",
//                 icon: <FaStickyNote />,
//                 label: "Notes",
//             },
//             {
//                 key: "announcements",
//                 icon: <FaBullhorn />,
//                 label: "Announcements",
//             },
//             {
//                 key: "reviews",
//                 icon: <FaStar />,
//                 label: "Reviews",
//             },
//             {
//                 key: "tools",
//                 icon: <FaTools />,
//                 label: "Learning Tools",
//             },
//         ],
//         []
//     );

//     if (!courseId) {
//         return (
//             <>
//                 <style>{CLASSROOM_STYLES}</style>

//                 <div className="cayep-classroom-empty">
//                     <div className="cayep-classroom-empty-icon">
//                         <FaBookOpen />
//                     </div>

//                     <h3>
//                         Classroom Unavailable
//                     </h3>

//                     <p>
//                         Select a course to access
//                         the learning environment.
//                     </p>
//                 </div>
//             </>
//         );
//     }

//     return (
//         <>
//             <style>
//                 {CLASSROOM_STYLES}
//             </style>

//             <div className="cayep-classroom-environment">
//                 <div className="cayep-classroom-header">
//                     <div className="cayep-classroom-header-main">
//                         <div className="cayep-classroom-header-icon">
//                             <FaGraduationCap />
//                         </div>

//                         <div className="cayep-classroom-header-copy">
//                             <span className="cayep-classroom-eyebrow">
//                                 {isManagement
//                                     ? "FACILITATOR VIEW"
//                                     : "LEARNER VIEW"}
//                             </span>

//                             <h2>
//                                 {course?.name ||
//                                     course?.title ||
//                                     "Course Classroom"}
//                             </h2>

//                             <div className="cayep-classroom-video-context">
//                                 <FaBookOpen />

//                                 <span>
//                                     {
//                                         videoContextLabel
//                                     }
//                                 </span>
//                             </div>
//                         </div>
//                     </div>

//                     <div className="cayep-classroom-status">
//                         <span className="cayep-classroom-status-dot" />

//                         <span>
//                             {isSectionVideo
//                                 ? "Section Classroom"
//                                 : "Course Classroom"}
//                         </span>
//                     </div>
//                 </div>

//                 {isPending && (
//                     <div className="cayep-classroom-approval-notice">
//                         <div className="cayep-classroom-approval-icon">
//                             <FaLock />
//                         </div>

//                         <div>
//                             <strong>
//                                 Course Access Pending
//                             </strong>

//                             <p>
//                                 This course is awaiting
//                                 approval before classroom
//                                 content can be accessed.
//                             </p>
//                         </div>
//                     </div>
//                 )}

//                 {!isPending && (
//                     <>
//                         <div className="cayep-classroom-tabs">
//                             {tabs.map(
//                                 (tab) => (
//                                     <button
//                                         key={
//                                             tab.key
//                                         }
//                                         type="button"
//                                         className={
//                                             activeTab ===
//                                             tab.key
//                                                 ? "active"
//                                                 : ""
//                                         }
//                                         onClick={() =>
//                                             setActiveTab(
//                                                 tab.key
//                                             )
//                                         }
//                                     >
//                                         <span className="cayep-classroom-tab-icon">
//                                             {
//                                                 tab.icon
//                                             }
//                                         </span>

//                                         <span className="cayep-classroom-tab-label">
//                                             {
//                                                 tab.label
//                                             }
//                                         </span>

//                                         {tab.key ===
//                                             "qa" &&
//                                             classroom
//                                                 .questions
//                                                 .length >
//                                                 0 && (
//                                                 <span className="cayep-classroom-tab-count">
//                                                     {
//                                                         classroom
//                                                             .questions
//                                                             .length
//                                                     }
//                                                 </span>
//                                             )}

//                                         {tab.key ===
//                                             "tools" &&
//                                             classroom
//                                                 .resources
//                                                 .length >
//                                                 0 && (
//                                                 <span className="cayep-classroom-tab-count">
//                                                     {
//                                                         classroom
//                                                             .resources
//                                                             .length
//                                                     }
//                                                 </span>
//                                             )}
//                                     </button>
//                                 )
//                             )}
//                         </div>

//                         <div className="cayep-classroom-panel">
//                             {loading ? (
//                                 <div className="cayep-classroom-loading">
//                                     <div className="cayep-classroom-spinner" />

//                                     <span>
//                                         Loading classroom...
//                                     </span>
//                                 </div>
//                             ) : (
//                                 <>
//                                     {activeTab ===
//                                         "overview" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaBookOpen />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         COURSE
//                                                         CONTENT
//                                                     </span>

//                                                     <h3>
//                                                         Overview
//                                                     </h3>

//                                                     <p>
//                                                         Learn more
//                                                         about this
//                                                         video and
//                                                         its training
//                                                         content.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .overview
//                                                 ?.content ? (
//                                                 <div className="cayep-classroom-rich-content">
//                                                     {String(
//                                                         classroom
//                                                             .overview
//                                                             .content
//                                                     )
//                                                         .split(
//                                                             /\r?\n/
//                                                         )
//                                                         .map(
//                                                             (
//                                                                 paragraph,
//                                                                 index
//                                                             ) =>
//                                                                 paragraph.trim() ? (
//                                                                     <p
//                                                                         key={
//                                                                             index
//                                                                         }
//                                                                     >
//                                                                         {
//                                                                             paragraph
//                                                                         }
//                                                                     </p>
//                                                                 ) : null
//                                                         )}
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaLightbulb />

//                                                     <h4>
//                                                         No Overview
//                                                         Yet
//                                                     </h4>

//                                                     <p>
//                                                         The
//                                                         facilitator
//                                                         has not
//                                                         added an
//                                                         overview
//                                                         for this
//                                                         video yet.
//                                                     </p>
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "qa" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaQuestionCircle />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         LEARNING
//                                                         COMMUNITY
//                                                     </span>

//                                                     <h3>
//                                                         Questions
//                                                         &amp;
//                                                         Answers
//                                                     </h3>

//                                                     <p>
//                                                         Ask questions,
//                                                         reply to
//                                                         other
//                                                         learners,
//                                                         and receive
//                                                         guidance
//                                                         from the
//                                                         facilitation
//                                                         team.
//                                                     </p>

//                                                     <div className="cayep-classroom-context-pill">
//                                                         <FaBookOpen />

//                                                         <span>
//                                                             {
//                                                                 videoContextLabel
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 </div>
//                                             </div>

//                                             <div className="cayep-classroom-community-banner">
//                                                 <div className="cayep-classroom-community-icon">
//                                                     <FaUsers />
//                                                 </div>

//                                                 <div>
//                                                     <strong>
//                                                         Learn
//                                                         Together
//                                                     </strong>

//                                                     <p>
//                                                         Ask a
//                                                         question,
//                                                         share an
//                                                         explanation,
//                                                         or build
//                                                         on another
//                                                         learner&apos;s
//                                                         idea.
//                                                         Facilitators
//                                                         can provide
//                                                         guidance
//                                                         when needed.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {isStudent && (
//                                                 <form
//                                                     className="cayep-classroom-question-form"
//                                                     onSubmit={
//                                                         submitQuestion
//                                                     }
//                                                 >
//                                                     <div className="cayep-classroom-form-avatar">
//                                                         <FaComment />
//                                                     </div>

//                                                     <div className="cayep-classroom-form-main">
//                                                         <label>
//                                                             Start a
//                                                             discussion
//                                                         </label>

//                                                         <textarea
//                                                             value={
//                                                                 questionText
//                                                             }
//                                                             onChange={(
//                                                                 event
//                                                             ) =>
//                                                                 setQuestionText(
//                                                                     event
//                                                                         .target
//                                                                         .value
//                                                                 )
//                                                             }
//                                                             placeholder="Share a question or thought about this video..."
//                                                             rows={
//                                                                 4
//                                                             }
//                                                             maxLength={
//                                                                 3000
//                                                             }
//                                                             disabled={
//                                                                 savingQuestion
//                                                             }
//                                                         />

//                                                         <div className="cayep-classroom-form-footer">
//                                                             <span>
//                                                                 {
//                                                                     questionText.length
//                                                                 }
//                                                                 /3000
//                                                             </span>

//                                                             <button
//                                                                 type="submit"
//                                                                 disabled={
//                                                                     savingQuestion ||
//                                                                     !questionText.trim()
//                                                                 }
//                                                             >
//                                                                 <FaPaperPlane />

//                                                                 {savingQuestion
//                                                                     ? "Posting..."
//                                                                     : "Post Question"}
//                                                             </button>
//                                                         </div>
//                                                     </div>
//                                                 </form>
//                                             )}

//                                             {isManagement && (
//                                                 <div className="cayep-classroom-teacher-notice">
//                                                     <div className="cayep-classroom-teacher-notice-icon">
//                                                         <FaChalkboardTeacher />
//                                                     </div>

//                                                     <div>
//                                                         <strong>
//                                                             Facilitation
//                                                             Discussion
//                                                         </strong>

//                                                         <p>
//                                                             Respond
//                                                             to learner
//                                                             questions
//                                                             and
//                                                             contribute
//                                                             guidance
//                                                             whenever
//                                                             clarification
//                                                             or another
//                                                             perspective
//                                                             will help.
//                                                         </p>
//                                                     </div>
//                                                 </div>
//                                             )}

//                                             {classroom
//                                                 .questions
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaUsers />

//                                                     <h4>
//                                                         {isManagement
//                                                             ? "No Learner Discussions Yet"
//                                                             : "Start the Conversation"}
//                                                     </h4>

//                                                     <p>
//                                                         {isManagement
//                                                             ? "Learner questions and ideas about this video will appear here."
//                                                             : "Be the first learner to ask a question or share an idea about this video."}
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-question-list">
//                                                     {classroom.questions.map(
//                                                         (
//                                                             question,
//                                                             questionIndex
//                                                         ) => {
//                                                             const replies =
//                                                                 getQuestionReplies(
//                                                                     question
//                                                                 );

//                                                             const questionKey =
//                                                                 question?.id ||
//                                                                 `question-${questionIndex}`;

//                                                             const expanded =
//                                                                 Boolean(
//                                                                     expandedQuestions[
//                                                                         questionKey
//                                                                     ]
//                                                                 );

//                                                             const replySaving =
//                                                                 String(
//                                                                     savingReplyId
//                                                                 ) ===
//                                                                 String(
//                                                                     question?.id
//                                                                 );

//                                                             return (
//                                                                 <article
//                                                                     className="cayep-community-thread"
//                                                                     key={
//                                                                         questionKey
//                                                                     }
//                                                                 >
//                                                                     <div className="cayep-community-question-row">
//                                                                         <div className="cayep-community-avatar">
//                                                                             <img
//                                                                                 src={getQuestionAvatar(
//                                                                                     question
//                                                                                 )}
//                                                                                 alt=""
//                                                                                 loading="lazy"
//                                                                                 onError={(
//                                                                                     event
//                                                                                 ) => {
//                                                                                     event.currentTarget.src =
//                                                                                         defaultAvatar;
//                                                                                 }}
//                                                                             />
//                                                                         </div>

//                                                                         <div className="cayep-community-message-area">
//                                                                             <div className="cayep-community-author-line">
//                                                                                 <strong>
//                                                                                     {getQuestionAuthor(
//                                                                                         question
//                                                                                     )}
//                                                                                 </strong>

//                                                                                 <span>
//                                                                                     {formatDate(
//                                                                                         question?.created_at ||
//                                                                                             question?.createdAt
//                                                                                     )}
//                                                                                 </span>
//                                                                             </div>

//                                                                             <div className="cayep-community-question-bubble">
//                                                                                 <span className="cayep-community-quote-mark">
//                                                                                     “
//                                                                                 </span>

//                                                                                 <p>
//                                                                                     {
//                                                                                         question?.question
//                                                                                     }
//                                                                                 </p>
//                                                                             </div>

//                                                                             <div className="cayep-community-message-actions">
//                                                                                 <button
//                                                                                     type="button"
//                                                                                     onClick={() =>
//                                                                                         toggleQuestion(
//                                                                                             questionKey
//                                                                                         )
//                                                                                     }
//                                                                                 >
//                                                                                     <FaReply />

//                                                                                     {expanded
//                                                                                         ? "Hide Discussion"
//                                                                                         : `Join Discussion${
//                                                                                               replies.length
//                                                                                                   ? ` (${replies.length})`
//                                                                                                   : ""
//                                                                                           }`}
//                                                                                 </button>
//                                                                             </div>
//                                                                         </div>
//                                                                     </div>

//                                                                     {expanded && (
//                                                                         <div className="cayep-community-conversation">
//                                                                             {replies.length >
//                                                                                 0 ? (
//                                                                                 replies.map(
//                                                                                     (
//                                                                                         reply,
//                                                                                         replyIndex
//                                                                                     ) => {
//                                                                                         const managementReply =
//                                                                                             isReplyFromManagement(
//                                                                                                 reply
//                                                                                             );

//                                                                                         return (
//                                                                                             <div
//                                                                                                 className={`cayep-community-reply-row ${
//                                                                                                     managementReply
//                                                                                                         ? "management-reply"
//                                                                                                         : "student-reply"
//                                                                                                 }`}
//                                                                                                 key={
//                                                                                                     reply?.id ||
//                                                                                                     replyIndex
//                                                                                                 }
//                                                                                             >
//                                                                                                 <div className="cayep-community-reply-avatar">
//                                                                                                     <img
//                                                                                                         src={getReplyAvatar(
//                                                                                                             reply
//                                                                                                         )}
//                                                                                                         alt=""
//                                                                                                         loading="lazy"
//                                                                                                         onError={(
//                                                                                                             event
//                                                                                                         ) => {
//                                                                                                             event.currentTarget.src =
//                                                                                                                 defaultAvatar;
//                                                                                                         }}
//                                                                                                     />
//                                                                                                 </div>

//                                                                                                 <div className="cayep-community-reply-area">
//                                                                                                     <div className="cayep-community-reply-meta">
//                                                                                                         <strong>
//                                                                                                             {getReplyAuthor(
//                                                                                                                 reply
//                                                                                                             )}
//                                                                                                         </strong>

//                                                                                                         <span className={`cayep-community-role-badge ${
//                                                                                                             managementReply
//                                                                                                                 ? "facilitator"
//                                                                                                                 : "learner"
//                                                                                                         }`}>
//                                                                                                             {managementReply ? (
//                                                                                                                 <>
//                                                                                                                     <FaChalkboardTeacher />
//                                                                                                                     Facilitator
//                                                                                                                 </>
//                                                                                                             ) : (
//                                                                                                                 <>
//                                                                                                                     <FaUser />
//                                                                                                                     Learner
//                                                                                                                 </>
//                                                                                                             )}
//                                                                                                         </span>

//                                                                                                         <span className="cayep-community-reply-date">
//                                                                                                             {formatDate(
//                                                                                                                 reply?.created_at ||
//                                                                                                                     reply?.createdAt
//                                                                                                             )}
//                                                                                                         </span>
//                                                                                                     </div>

//                                                                                                     <div className="cayep-community-reply-bubble">
//                                                                                                         <p>
//                                                                                                             {
//                                                                                                                 reply?.reply
//                                                                                                             }
//                                                                                                         </p>
//                                                                                                     </div>
//                                                                                                 </div>
//                                                                                             </div>
//                                                                                         );
//                                                                                     }
//                                                                                 )
//                                                                             ) : (
//                                                                                 <div className="cayep-community-no-replies">
//                                                                                     <FaComment />

//                                                                                     <span>
//                                                                                         No
//                                                                                         replies
//                                                                                         yet.
//                                                                                         Start
//                                                                                         the
//                                                                                         conversation
//                                                                                         and
//                                                                                         share
//                                                                                         your
//                                                                                         perspective.
//                                                                                     </span>
//                                                                                 </div>
//                                                                             )}

//                                                                             {(isStudent ||
//                                                                                 isManagement) && (
//                                                                                 <form
//                                                                                     className="cayep-community-reply-form"
//                                                                                     onSubmit={(
//                                                                                         event
//                                                                                     ) =>
//                                                                                         submitReply(
//                                                                                             event,
//                                                                                             question?.id
//                                                                                         )
//                                                                                     }
//                                                                                 >
//                                                                                     <div className="cayep-community-reply-form-avatar">
//                                                                                         {isManagement ? (
//                                                                                             <FaChalkboardTeacher />
//                                                                                         ) : (
//                                                                                             <FaUser />
//                                                                                         )}
//                                                                                     </div>

//                                                                                     <div className="cayep-community-reply-form-main">
//                                                                                         <textarea
//                                                                                             value={
//                                                                                                 replyTexts[
//                                                                                                     question?.id
//                                                                                                 ] ||
//                                                                                                 ""
//                                                                                             }
//                                                                                             onChange={(
//                                                                                                 event
//                                                                                             ) =>
//                                                                                                 setReplyTexts(
//                                                                                                     (
//                                                                                                         previous
//                                                                                                     ) => ({
//                                                                                                         ...previous,
//                                                                                                         [question?.id]:
//                                                                                                             event
//                                                                                                                 .target
//                                                                                                                 .value,
//                                                                                                     })
//                                                                                                 )
//                                                                                             }
//                                                                                             placeholder={
//                                                                                                 isManagement
//                                                                                                     ? "Share guidance, an explanation, or another perspective..."
//                                                                                                     : "Reply to this learner..."
//                                                                                             }
//                                                                                             rows={
//                                                                                                 3
//                                                                                             }
//                                                                                             maxLength={
//                                                                                                 3000
//                                                                                             }
//                                                                                             disabled={
//                                                                                                 replySaving
//                                                                                             }
//                                                                                         />

//                                                                                         <div className="cayep-community-reply-form-footer">
//                                                                                             <span>
//                                                                                                 {isManagement
//                                                                                                     ? "Facilitator response"
//                                                                                                     : "Learner response"}
//                                                                                             </span>

//                                                                                             <button
//                                                                                                 type="submit"
//                                                                                                 disabled={
//                                                                                                     replySaving ||
//                                                                                                     !String(
//                                                                                                         replyTexts[
//                                                                                                             question?.id
//                                                                                                         ] ||
//                                                                                                             ""
//                                                                                                     ).trim()
//                                                                                                 }
//                                                                                             >
//                                                                                                 <FaPaperPlane />

//                                                                                                 {replySaving
//                                                                                                     ? "Sending..."
//                                                                                                     : "Reply"}
//                                                                                             </button>
//                                                                                         </div>
//                                                                                     </div>
//                                                                                 </form>
//                                                                             )}
//                                                                         </div>
//                                                                     )}
//                                                                 </article>
//                                                             );
//                                                         }
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "notes" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaStickyNote />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         PRIVATE
//                                                         LEARNING
//                                                     </span>

//                                                     <h3>
//                                                         My Notes
//                                                     </h3>

//                                                     <p>
//                                                         Your notes
//                                                         are private
//                                                         and saved
//                                                         specifically
//                                                         for this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {isStudent ? (
//                                                 <form
//                                                     className="cayep-classroom-notes-form"
//                                                     onSubmit={
//                                                         saveNote
//                                                     }
//                                                 >
//                                                     <div className="cayep-classroom-notes-toolbar">
//                                                         <span>
//                                                             <FaPen />
//                                                             Personal
//                                                             notes
//                                                         </span>

//                                                         {noteSaved && (
//                                                             <span className="cayep-classroom-note-saved">
//                                                                 <FaCheckCircle />
//                                                                 Saved
//                                                             </span>
//                                                         )}
//                                                     </div>

//                                                     <textarea
//                                                         value={
//                                                             noteContent
//                                                         }
//                                                         onChange={(
//                                                             event
//                                                         ) =>
//                                                             setNoteContent(
//                                                                 event
//                                                                     .target
//                                                                     .value
//                                                             )
//                                                         }
//                                                         placeholder="Write your private notes for this video..."
//                                                         rows={
//                                                             12
//                                                         }
//                                                         disabled={
//                                                             savingNote
//                                                         }
//                                                     />

//                                                     <div className="cayep-classroom-notes-footer">
//                                                         <span>
//                                                             <FaLock />
//                                                             Only you
//                                                             can view
//                                                             these
//                                                             notes.
//                                                         </span>

//                                                         <button
//                                                             type="submit"
//                                                             disabled={
//                                                                 savingNote
//                                                             }
//                                                         >
//                                                             <FaSave />

//                                                             {savingNote
//                                                                 ? "Saving..."
//                                                                 : "Save Notes"}
//                                                         </button>
//                                                     </div>
//                                                 </form>
//                                             ) : (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaLock />

//                                                     <h4>
//                                                         Student
//                                                         Notes
//                                                     </h4>

//                                                     <p>
//                                                         Notes are a
//                                                         private
//                                                         student
//                                                         learning
//                                                         feature.
//                                                     </p>
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "announcements" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaBullhorn />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         UPDATES
//                                                     </span>

//                                                     <h3>
//                                                         Announcements
//                                                     </h3>

//                                                     <p>
//                                                         Important
//                                                         information
//                                                         from the
//                                                         facilitator
//                                                         for this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .announcements
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaBullhorn />

//                                                     <h4>
//                                                         No
//                                                         Announcements
//                                                     </h4>

//                                                     <p>
//                                                         There are no
//                                                         announcements
//                                                         for this
//                                                         video at the
//                                                         moment.
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-announcement-list">
//                                                     {classroom.announcements.map(
//                                                         (
//                                                             announcement,
//                                                             index
//                                                         ) => (
//                                                             <article
//                                                                 className="cayep-classroom-announcement-card"
//                                                                 key={
//                                                                     announcement?.id ||
//                                                                     index
//                                                                 }
//                                                             >
//                                                                 <div className="cayep-classroom-announcement-icon">
//                                                                     <FaBullhorn />
//                                                                 </div>

//                                                                 <div className="cayep-classroom-announcement-body">
//                                                                     <div className="cayep-classroom-announcement-meta">
//                                                                         <span>
//                                                                             {formatDate(
//                                                                                 announcement?.created_at ||
//                                                                                     announcement?.createdAt
//                                                                             )}
//                                                                         </span>
//                                                                     </div>

//                                                                     <h4>
//                                                                         {
//                                                                             announcement?.title
//                                                                         }
//                                                                     </h4>

//                                                                     <p>
//                                                                         {
//                                                                             announcement?.content
//                                                                         }
//                                                                     </p>
//                                                                 </div>
//                                                             </article>
//                                                         )
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "reviews" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaStar />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         FACILITATOR
//                                                         FEEDBACK
//                                                     </span>

//                                                     <h3>
//                                                         Reviews
//                                                     </h3>

//                                                     <p>
//                                                         Guidance and
//                                                         feedback
//                                                         associated
//                                                         with this
//                                                         video.
//                                                     </p>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .reviews
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaStar />

//                                                     <h4>
//                                                         No Reviews
//                                                         Yet
//                                                     </h4>

//                                                     <p>
//                                                         No facilitator
//                                                         review has
//                                                         been added
//                                                         for this
//                                                         video yet.
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-review-list">
//                                                     {classroom.reviews.map(
//                                                         (
//                                                             review,
//                                                             index
//                                                         ) => (
//                                                             <article
//                                                                 className="cayep-classroom-review-card"
//                                                                 key={
//                                                                     review?.id ||
//                                                                     index
//                                                                 }
//                                                             >
//                                                                 <div className="cayep-classroom-review-header">
//                                                                     <div className="cayep-classroom-review-icon">
//                                                                         <FaStar />
//                                                                     </div>

//                                                                     <div>
//                                                                         <strong>
//                                                                             Facilitator
//                                                                             Review
//                                                                         </strong>

//                                                                         <span>
//                                                                             {formatDate(
//                                                                                 review?.created_at ||
//                                                                                     review?.createdAt
//                                                                             )}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>

//                                                                 <p>
//                                                                     {
//                                                                         review?.content
//                                                                     }
//                                                                 </p>
//                                                             </article>
//                                                         )
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}

//                                     {activeTab ===
//                                         "tools" && (
//                                         <section className="cayep-classroom-content-section">
//                                             <div className="cayep-classroom-section-heading">
//                                                 <div className="cayep-classroom-heading-icon">
//                                                     <FaTools />
//                                                 </div>

//                                                 <div>
//                                                     <span className="cayep-classroom-section-kicker">
//                                                         COURSE
//                                                         MATERIALS
//                                                     </span>

//                                                     <h3>
//                                                         Learning
//                                                         Tools
//                                                     </h3>

//                                                     <p>
//                                                         Download
//                                                         learning
//                                                         materials
//                                                         provided
//                                                         specifically
//                                                         for this
//                                                         video.
//                                                     </p>

//                                                     <div className="cayep-classroom-context-pill">
//                                                         <FaBookOpen />

//                                                         <span>
//                                                             {
//                                                                 videoContextLabel
//                                                             }
//                                                         </span>
//                                                     </div>
//                                                 </div>
//                                             </div>

//                                             {classroom
//                                                 .resources
//                                                 .length ===
//                                             0 ? (
//                                                 <div className="cayep-classroom-empty-panel">
//                                                     <FaTools />

//                                                     <h4>
//                                                         No Learning
//                                                         Resources
//                                                     </h4>

//                                                     <p>
//                                                         {isSectionVideo
//                                                             ? "No files have been uploaded for this section yet."
//                                                             : "No files have been uploaded for the Introduction Video yet."}
//                                                     </p>
//                                                 </div>
//                                             ) : (
//                                                 <div className="cayep-classroom-resource-list">
//                                                     {classroom.resources.map(
//                                                         (
//                                                             resource,
//                                                             index
//                                                         ) => {
//                                                             const filename =
//                                                                 resource?.original_filename ||
//                                                                 resource?.originalFilename ||
//                                                                 resource?.title ||
//                                                                 "Learning Resource";

//                                                             const extension =
//                                                                 resource?.file_extension ||
//                                                                 resource?.extension ||
//                                                                 "";

//                                                             const fileSize =
//                                                                 formatFileSize(
//                                                                     resource?.file_size ??
//                                                                         resource?.fileSize
//                                                                 );

//                                                             const isDownloading =
//                                                                 String(
//                                                                     downloadingResourceId
//                                                                 ) ===
//                                                                 String(
//                                                                     resource?.id
//                                                                 );

//                                                             return (
//                                                                 <article
//                                                                     className="cayep-classroom-resource-card"
//                                                                     key={
//                                                                         resource?.id ||
//                                                                         index
//                                                                     }
//                                                                 >
//                                                                     <div className="cayep-classroom-resource-icon">
//                                                                         {getFileIcon(
//                                                                             resource
//                                                                         )}
//                                                                     </div>

//                                                                     <div className="cayep-classroom-resource-info">
//                                                                         <div className="cayep-classroom-resource-top">
//                                                                             <h4>
//                                                                                 {
//                                                                                     resource?.title
//                                                                                 }
//                                                                             </h4>

//                                                                             {extension && (
//                                                                                 <span className="cayep-classroom-resource-extension">
//                                                                                     {String(
//                                                                                         extension
//                                                                                     )
//                                                                                         .replace(
//                                                                                             ".",
//                                                                                             ""
//                                                                                         )
//                                                                                         .toUpperCase()}
//                                                                                 </span>
//                                                                             )}
//                                                                         </div>

//                                                                         <p className="cayep-classroom-resource-filename">
//                                                                             {
//                                                                                 filename
//                                                                             }
//                                                                         </p>

//                                                                         {resource?.description && (
//                                                                             <p className="cayep-classroom-resource-description">
//                                                                                 {
//                                                                                     resource.description
//                                                                                 }
//                                                                             </p>
//                                                                         )}

//                                                                         <div className="cayep-classroom-resource-meta">
//                                                                             <span>
//                                                                                 {
//                                                                                     fileSize
//                                                                                 }
//                                                                             </span>

//                                                                             {resource?.created_at && (
//                                                                                 <>
//                                                                                     <span className="cayep-resource-meta-dot">
//                                                                                         •
//                                                                                     </span>

//                                                                                     <span>
//                                                                                         Uploaded{" "}
//                                                                                         {formatDate(
//                                                                                             resource.created_at
//                                                                                         )}
//                                                                                     </span>
//                                                                                 </>
//                                                                             )}
//                                                                         </div>
//                                                                     </div>

//                                                                     <button
//                                                                         type="button"
//                                                                         className="cayep-classroom-resource-download"
//                                                                         onClick={() =>
//                                                                             downloadResource(
//                                                                                 resource
//                                                                             )
//                                                                         }
//                                                                         disabled={
//                                                                             isDownloading
//                                                                         }
//                                                                     >
//                                                                         <FaDownload />

//                                                                         <span>
//                                                                             {isDownloading
//                                                                                 ? "Downloading..."
//                                                                                 : "Download"}
//                                                                         </span>
//                                                                     </button>
//                                                                 </article>
//                                                             );
//                                                         }
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </section>
//                                     )}
//                                 </>
//                             )}
//                         </div>
//                     </>
//                 )}
//             </div>
//         </>
//     );
// };

// const CLASSROOM_STYLES = `
// .cayep-classroom-environment {
//     width: 100%;
//     max-width: 1320px;
//     margin: 0 auto;
//     padding: 0;
//     color: #142536;
//     box-sizing: border-box;
// }

// .cayep-classroom-environment *,
// .cayep-classroom-environment *::before,
// .cayep-classroom-environment *::after {
//     box-sizing: border-box;
// }

// .cayep-classroom-header {
//     width: 100%;
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 24px;
//     padding: 24px 26px;
//     margin-bottom: 18px;
//     border: 1px solid #E0E7EC;
//     border-radius: 18px;
//     background: linear-gradient(
//         135deg,
//         #062A4B 0%,
//         #063A6B 100%
//     );
//     box-shadow: 0 10px 30px rgba(6, 42, 75, 0.12);
// }

// .cayep-classroom-header-main {
//     min-width: 0;
//     display: flex;
//     align-items: center;
//     gap: 16px;
// }

// .cayep-classroom-header-icon {
//     width: 56px;
//     height: 56px;
//     flex: 0 0 56px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 15px;
//     background: rgba(246, 201, 68, 0.14);
//     color: #F6C944;
//     font-size: 24px;
//     border: 1px solid rgba(246, 201, 68, 0.22);
// }

// .cayep-classroom-header-copy {
//     min-width: 0;
// }

// .cayep-classroom-eyebrow {
//     display: block;
//     margin-bottom: 5px;
//     color: #F6C944;
//     font-size: 10px;
//     line-height: 1.2;
//     font-weight: 800;
//     letter-spacing: 1.4px;
// }

// .cayep-classroom-header-copy h2 {
//     margin: 0;
//     color: #F0EDEA;
//     font-size: clamp(20px, 2vw, 28px);
//     line-height: 1.2;
//     font-weight: 800;
//     word-break: break-word;
// }

// .cayep-classroom-video-context {
//     display: flex;
//     align-items: center;
//     gap: 7px;
//     margin-top: 8px;
//     color: rgba(240, 237, 234, 0.78);
//     font-size: 13px;
//     line-height: 1.45;
// }

// .cayep-classroom-video-context svg {
//     flex: 0 0 auto;
//     color: #EFA81C;
// }

// .cayep-classroom-status {
//     flex: 0 0 auto;
//     display: inline-flex;
//     align-items: center;
//     gap: 8px;
//     min-height: 36px;
//     padding: 8px 12px;
//     border-radius: 999px;
//     color: #F0EDEA;
//     background: rgba(255, 255, 255, 0.08);
//     border: 1px solid rgba(255, 255, 255, 0.12);
//     font-size: 11px;
//     font-weight: 700;
//     white-space: nowrap;
// }

// .cayep-classroom-status-dot {
//     width: 8px;
//     height: 8px;
//     border-radius: 50%;
//     background: #4FAE22;
//     box-shadow: 0 0 0 4px rgba(79, 174, 34, 0.13);
// }

// .cayep-classroom-approval-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 14px;
//     width: 100%;
//     padding: 20px;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     background: #FFFDF6;
//     box-shadow: 0 5px 20px rgba(20, 37, 54, 0.05);
// }

// .cayep-classroom-approval-icon {
//     width: 42px;
//     height: 42px;
//     flex: 0 0 42px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 12px;
//     color: #C0750D;
//     background: rgba(192, 117, 13, 0.1);
// }

// .cayep-classroom-approval-notice strong {
//     display: block;
//     margin-bottom: 5px;
//     color: #142536;
//     font-size: 15px;
// }

// .cayep-classroom-approval-notice p {
//     margin: 0;
//     color: #6B7280;
//     font-size: 13px;
//     line-height: 1.65;
// }

// .cayep-classroom-tabs {
//     width: 100%;
//     display: flex;
//     align-items: stretch;
//     gap: 4px;
//     padding: 5px;
//     margin-bottom: 14px;
//     overflow-x: auto;
//     overflow-y: hidden;
//     scrollbar-width: thin;
//     border: 1px solid #E0E7EC;
//     border-radius: 15px;
//     background: #FFFFFF;
// }

// .cayep-classroom-tabs::-webkit-scrollbar {
//     height: 4px;
// }

// .cayep-classroom-tabs::-webkit-scrollbar-thumb {
//     background: #C4C7C7;
//     border-radius: 10px;
// }

// .cayep-classroom-tabs button {
//     min-width: max-content;
//     flex: 1 0 auto;
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     gap: 8px;
//     min-height: 46px;
//     padding: 10px 14px;
//     border: 0;
//     border-radius: 11px;
//     background: transparent;
//     color: #6B7280;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 12px;
//     font-weight: 700;
//     transition:
//         background 0.18s ease,
//         color 0.18s ease,
//         transform 0.18s ease;
// }

// .cayep-classroom-tabs button:hover {
//     color: #062A4B;
//     background: #F4F7F9;
// }

// .cayep-classroom-tabs button.active {
//     color: #FFFFFF;
//     background: #062A4B;
//     box-shadow: 0 5px 14px rgba(6, 42, 75, 0.16);
// }

// .cayep-classroom-tab-icon {
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     font-size: 14px;
// }

// .cayep-classroom-tab-count {
//     min-width: 21px;
//     height: 21px;
//     padding: 0 6px;
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 999px;
//     background: #EFA81C;
//     color: #142536;
//     font-size: 10px;
//     font-weight: 800;
// }

// .cayep-classroom-panel {
//     width: 100%;
//     min-height: 300px;
//     overflow: visible;
//     border: 1px solid #E0E7EC;
//     border-radius: 18px;
//     background: #FFFFFF;
//     box-shadow: 0 8px 28px rgba(20, 37, 54, 0.05);
// }

// .cayep-classroom-loading {
//     min-height: 330px;
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     gap: 14px;
//     padding: 40px 20px;
//     color: #6B7280;
//     font-size: 13px;
//     font-weight: 600;
// }

// .cayep-classroom-spinner {
//     width: 38px;
//     height: 38px;
//     border: 3px solid #E0E7EC;
//     border-top-color: #062A4B;
//     border-right-color: #EFA81C;
//     border-radius: 50%;
//     animation: cayepClassroomSpin 0.8s linear infinite;
// }

// @keyframes cayepClassroomSpin {
//     to {
//         transform: rotate(360deg);
//     }
// }

// .cayep-classroom-content-section {
//     width: 100%;
//     padding: 30px;
// }

// .cayep-classroom-section-heading {
//     display: flex;
//     align-items: flex-start;
//     gap: 15px;
//     margin-bottom: 24px;
// }

// .cayep-classroom-heading-icon {
//     width: 46px;
//     height: 46px;
//     flex: 0 0 46px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 13px;
//     background: #F4F7F9;
//     color: #063A6B;
//     border: 1px solid #E0E7EC;
//     font-size: 17px;
// }

// .cayep-classroom-section-heading > div:last-child {
//     min-width: 0;
// }

// .cayep-classroom-section-kicker {
//     display: block;
//     margin-bottom: 5px;
//     color: #C0750D;
//     font-size: 9px;
//     font-weight: 800;
//     letter-spacing: 1.2px;
// }

// .cayep-classroom-section-heading h3 {
//     margin: 0;
//     color: #142536;
//     font-size: 22px;
//     line-height: 1.25;
//     font-weight: 800;
// }

// .cayep-classroom-section-heading p {
//     max-width: 780px;
//     margin: 7px 0 0;
//     color: #6B7280;
//     font-size: 13px;
//     line-height: 1.7;
// }

// .cayep-classroom-context-pill {
//     width: fit-content;
//     max-width: 100%;
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     margin-top: 12px;
//     padding: 7px 11px;
//     border-radius: 999px;
//     background: #F4F7F9;
//     border: 1px solid #E0E7EC;
//     color: #063A6B;
//     font-size: 11px;
//     font-weight: 700;
// }

// .cayep-classroom-context-pill svg {
//     flex: 0 0 auto;
//     color: #EFA81C;
// }

// .cayep-classroom-rich-content {
//     width: 100%;
//     padding: 23px;
//     border: 1px solid #E0E7EC;
//     border-radius: 15px;
//     background: #FBFCFD;
// }

// .cayep-classroom-rich-content p {
//     margin: 0 0 14px;
//     color: #394A59;
//     font-size: 14px;
//     line-height: 1.85;
//     white-space: pre-wrap;
// }

// .cayep-classroom-rich-content p:last-child {
//     margin-bottom: 0;
// }

// .cayep-classroom-empty-panel {
//     width: 100%;
//     min-height: 220px;
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     padding: 35px 24px;
//     text-align: center;
//     border: 1px dashed #C4C7C7;
//     border-radius: 16px;
//     background: #FBFCFD;
// }

// .cayep-classroom-empty-panel > svg {
//     margin-bottom: 12px;
//     color: #EFA81C;
//     font-size: 28px;
// }

// .cayep-classroom-empty-panel h4 {
//     margin: 0 0 7px;
//     color: #142536;
//     font-size: 15px;
//     font-weight: 800;
// }

// .cayep-classroom-empty-panel p {
//     max-width: 550px;
//     margin: 0;
//     color: #6B7280;
//     font-size: 13px;
//     line-height: 1.7;
// }

// .cayep-classroom-community-banner {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     margin-bottom: 22px;
//     padding: 17px;
//     border: 1px solid #DCE7F0;
//     border-radius: 15px;
//     background: #F5F9FC;
// }

// .cayep-classroom-community-icon {
//     width: 40px;
//     height: 40px;
//     flex: 0 0 40px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 11px;
//     background: #063A6B;
//     color: #FFFFFF;
// }

// .cayep-classroom-community-banner strong {
//     display: block;
//     margin-bottom: 4px;
//     color: #142536;
//     font-size: 13px;
// }

// .cayep-classroom-community-banner p {
//     margin: 0;
//     color: #647180;
//     font-size: 12px;
//     line-height: 1.7;
// }

// .cayep-classroom-question-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     margin-bottom: 25px;
//     padding: 17px;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     background: #FFFFFF;
// }

// .cayep-classroom-form-avatar {
//     width: 42px;
//     height: 42px;
//     flex: 0 0 42px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     background: #062A4B;
//     color: #F6C944;
//     font-size: 14px;
// }

// .cayep-classroom-form-main {
//     min-width: 0;
//     flex: 1;
// }

// .cayep-classroom-form-main label {
//     display: block;
//     margin-bottom: 8px;
//     color: #142536;
//     font-size: 13px;
//     font-weight: 800;
// }

// .cayep-classroom-form-main textarea,
// .cayep-community-reply-form-main textarea,
// .cayep-classroom-notes-form textarea {
//     width: 100%;
//     display: block;
//     resize: vertical;
//     border: 1px solid #D7E0E7;
//     border-radius: 11px;
//     outline: none;
//     background: #FBFCFD;
//     color: #142536;
//     font-family: inherit;
//     font-size: 13px;
//     line-height: 1.65;
//     transition:
//         border-color 0.18s ease,
//         box-shadow 0.18s ease,
//         background 0.18s ease;
// }

// .cayep-classroom-form-main textarea {
//     min-height: 105px;
//     padding: 13px 14px;
// }

// .cayep-classroom-form-main textarea:focus,
// .cayep-community-reply-form-main textarea:focus,
// .cayep-classroom-notes-form textarea:focus {
//     border-color: #0F6DAB;
//     background: #FFFFFF;
//     box-shadow: 0 0 0 3px rgba(15, 109, 171, 0.09);
// }

// .cayep-classroom-form-main textarea:disabled,
// .cayep-community-reply-form-main textarea:disabled,
// .cayep-classroom-notes-form textarea:disabled {
//     opacity: 0.65;
//     cursor: not-allowed;
// }

// .cayep-classroom-form-footer,
// .cayep-community-reply-form-footer,
// .cayep-classroom-notes-footer {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 12px;
//     margin-top: 10px;
// }

// .cayep-classroom-form-footer > span {
//     color: #8994A0;
//     font-size: 10px;
//     font-weight: 600;
// }

// .cayep-classroom-form-footer button,
// .cayep-community-reply-form-footer button,
// .cayep-classroom-notes-footer button {
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     gap: 7px;
//     min-height: 38px;
//     padding: 9px 15px;
//     border: 0;
//     border-radius: 9px;
//     background: #062A4B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 11px;
//     font-weight: 800;
//     transition:
//         background 0.18s ease,
//         transform 0.18s ease,
//         opacity 0.18s ease;
// }

// .cayep-classroom-form-footer button:hover:not(:disabled),
// .cayep-community-reply-form-footer button:hover:not(:disabled),
// .cayep-classroom-notes-footer button:hover:not(:disabled) {
//     background: #063A6B;
//     transform: translateY(-1px);
// }

// .cayep-classroom-form-footer button:disabled,
// .cayep-community-reply-form-footer button:disabled,
// .cayep-classroom-notes-footer button:disabled {
//     opacity: 0.55;
//     cursor: not-allowed;
//     transform: none;
// }

// .cayep-classroom-teacher-notice {
//     display: flex;
//     align-items: flex-start;
//     gap: 12px;
//     margin-bottom: 22px;
//     padding: 15px;
//     border-radius: 14px;
//     border: 1px solid #E7E0CA;
//     background: #FFFCF2;
// }

// .cayep-classroom-teacher-notice-icon {
//     width: 38px;
//     height: 38px;
//     flex: 0 0 38px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 10px;
//     background: #F6C944;
//     color: #142536;
// }

// .cayep-classroom-teacher-notice strong {
//     display: block;
//     margin-bottom: 4px;
//     color: #142536;
//     font-size: 12px;
// }

// .cayep-classroom-teacher-notice p {
//     margin: 0;
//     color: #6B7280;
//     font-size: 12px;
//     line-height: 1.65;
// }

// .cayep-classroom-question-list {
//     display: flex;
//     flex-direction: column;
//     gap: 15px;
// }

// .cayep-community-thread {
//     width: 100%;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     background: #FFFFFF;
//     overflow: hidden;
// }

// .cayep-community-question-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 13px;
//     padding: 17px;
// }

// .cayep-community-avatar,
// .cayep-community-reply-avatar {
//     flex: 0 0 auto;
//     overflow: hidden;
//     border-radius: 50%;
//     background: #E7EDF2;
// }

// .cayep-community-avatar {
//     width: 42px;
//     height: 42px;
// }

// .cayep-community-reply-avatar {
//     width: 34px;
//     height: 34px;
// }

// .cayep-community-avatar img,
// .cayep-community-reply-avatar img {
//     width: 100%;
//     height: 100%;
//     display: block;
//     object-fit: cover;
// }

// .cayep-community-message-area,
// .cayep-community-reply-area {
//     min-width: 0;
//     flex: 1;
// }

// .cayep-community-author-line,
// .cayep-community-reply-meta {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 7px;
// }

// .cayep-community-author-line strong,
// .cayep-community-reply-meta strong {
//     color: #142536;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-community-author-line span,
// .cayep-community-reply-date {
//     color: #8994A0;
//     font-size: 10px;
// }

// .cayep-community-question-bubble {
//     position: relative;
//     margin-top: 8px;
//     padding: 14px 17px 14px 27px;
//     border-radius: 5px 14px 14px 14px;
//     background: #F4F7F9;
//     border: 1px solid #E3E9ED;
// }

// .cayep-community-question-bubble p {
//     position: relative;
//     z-index: 1;
//     margin: 0;
//     color: #263746;
//     font-size: 13px;
//     line-height: 1.75;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
// }

// .cayep-community-quote-mark {
//     position: absolute;
//     left: 9px;
//     top: 5px;
//     color: #CBA454;
//     font-family: Georgia, serif;
//     font-size: 22px;
//     line-height: 1;
// }

// .cayep-community-message-actions {
//     display: flex;
//     align-items: center;
//     margin-top: 8px;
// }

// .cayep-community-message-actions button {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     padding: 5px 7px;
//     border: 0;
//     background: transparent;
//     color: #063A6B;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-community-message-actions button:hover {
//     color: #C0750D;
// }

// .cayep-community-conversation {
//     padding: 0 17px 17px 71px;
//     border-top: 1px solid #EDF1F4;
//     background: #FBFCFD;
// }

// .cayep-community-reply-row {
//     display: flex;
//     align-items: flex-start;
//     gap: 10px;
//     padding: 15px 0 0;
// }

// .cayep-community-reply-bubble {
//     margin-top: 5px;
//     padding: 11px 13px;
//     border-radius: 5px 11px 11px 11px;
//     border: 1px solid #E2E8ED;
//     background: #FFFFFF;
// }

// .cayep-community-reply-bubble p {
//     margin: 0;
//     color: #374957;
//     font-size: 12px;
//     line-height: 1.7;
//     white-space: pre-wrap;
//     overflow-wrap: anywhere;
// }

// .cayep-community-role-badge {
//     display: inline-flex;
//     align-items: center;
//     gap: 4px;
//     padding: 3px 6px;
//     border-radius: 999px;
//     font-size: 9px;
//     font-weight: 800;
// }

// .cayep-community-role-badge.facilitator {
//     color: #063A6B;
//     background: #E8F1F8;
// }

// .cayep-community-role-badge.learner {
//     color: #4F5D29;
//     background: #F1F5E9;
// }

// .cayep-community-no-replies {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     padding: 15px 0 2px;
//     color: #8994A0;
//     font-size: 11px;
// }

// .cayep-community-reply-form {
//     display: flex;
//     align-items: flex-start;
//     gap: 10px;
//     margin-top: 16px;
//     padding: 12px;
//     border: 1px solid #E0E7EC;
//     border-radius: 13px;
//     background: #FFFFFF;
// }

// .cayep-community-reply-form-avatar {
//     width: 34px;
//     height: 34px;
//     flex: 0 0 34px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 50%;
//     background: #062A4B;
//     color: #F6C944;
//     font-size: 12px;
// }

// .cayep-community-reply-form-main {
//     min-width: 0;
//     flex: 1;
// }

// .cayep-community-reply-form-main textarea {
//     min-height: 72px;
//     padding: 10px 12px;
// }

// .cayep-community-reply-form-footer > span {
//     color: #8994A0;
//     font-size: 10px;
//     font-weight: 600;
// }

// .cayep-classroom-notes-form {
//     width: 100%;
//     overflow: hidden;
//     border: 1px solid #E0E7EC;
//     border-radius: 16px;
//     background: #FFFFFF;
// }

// .cayep-classroom-notes-toolbar {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     gap: 12px;
//     padding: 13px 16px;
//     border-bottom: 1px solid #E8EDF0;
//     background: #F8FAFB;
// }

// .cayep-classroom-notes-toolbar > span:first-child {
//     display: inline-flex;
//     align-items: center;
//     gap: 7px;
//     color: #063A6B;
//     font-size: 12px;
//     font-weight: 800;
// }

// .cayep-classroom-note-saved {
//     display: inline-flex;
//     align-items: center;
//     gap: 5px;
//     color: #4FAE22;
//     font-size: 11px;
//     font-weight: 800;
// }

// .cayep-classroom-notes-form textarea {
//     min-height: 270px;
//     padding: 18px;
//     resize: vertical;
//     border: 0;
//     border-radius: 0;
//     background: #FFFFFF;
// }

// .cayep-classroom-notes-form textarea:focus {
//     border: 0;
//     box-shadow: none;
// }

// .cayep-classroom-notes-footer {
//     padding: 12px 16px;
//     border-top: 1px solid #E8EDF0;
//     background: #F8FAFB;
// }

// .cayep-classroom-notes-footer > span {
//     display: inline-flex;
//     align-items: center;
//     gap: 6px;
//     color: #7A8691;
//     font-size: 10px;
//     font-weight: 600;
// }

// .cayep-classroom-announcement-list,
// .cayep-classroom-review-list,
// .cayep-classroom-resource-list {
//     display: flex;
//     flex-direction: column;
//     gap: 13px;
// }

// .cayep-classroom-announcement-card {
//     display: flex;
//     align-items: flex-start;
//     gap: 14px;
//     padding: 17px;
//     border: 1px solid #E0E7EC;
//     border-radius: 15px;
//     background: #FFFFFF;
// }

// .cayep-classroom-announcement-icon {
//     width: 42px;
//     height: 42px;
//     flex: 0 0 42px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 11px;
//     background: #FFF8E1;
//     color: #C0750D;
// }

// .cayep-classroom-announcement-body {
//     min-width: 0;
//     flex: 1;
// }

// .cayep-classroom-announcement-meta {
//     margin-bottom: 5px;
//     color: #8994A0;
//     font-size: 10px;
// }

// .cayep-classroom-announcement-body h4 {
//     margin: 0 0 6px;
//     color: #142536;
//     font-size: 14px;
//     font-weight: 800;
// }

// .cayep-classroom-announcement-body p {
//     margin: 0;
//     color: #5E6D79;
//     font-size: 12px;
//     line-height: 1.75;
//     white-space: pre-wrap;
// }

// .cayep-classroom-review-card {
//     padding: 18px;
//     border: 1px solid #E0E7EC;
//     border-radius: 15px;
//     background: #FFFFFF;
// }

// .cayep-classroom-review-header {
//     display: flex;
//     align-items: center;
//     gap: 11px;
//     margin-bottom: 13px;
// }

// .cayep-classroom-review-icon {
//     width: 40px;
//     height: 40px;
//     flex: 0 0 40px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 11px;
//     background: #FFF8E1;
//     color: #EFA81C;
// }

// .cayep-classroom-review-header div:last-child {
//     display: flex;
//     flex-direction: column;
//     gap: 3px;
// }

// .cayep-classroom-review-header strong {
//     color: #142536;
//     font-size: 12px;
// }

// .cayep-classroom-review-header span {
//     color: #8994A0;
//     font-size: 10px;
// }

// .cayep-classroom-review-card > p {
//     margin: 0;
//     color: #536372;
//     font-size: 13px;
//     line-height: 1.8;
//     white-space: pre-wrap;
// }

// .cayep-classroom-resource-card {
//     display: flex;
//     align-items: center;
//     gap: 14px;
//     width: 100%;
//     padding: 15px;
//     border: 1px solid #E0E7EC;
//     border-radius: 15px;
//     background: #FFFFFF;
//     transition:
//         border-color 0.18s ease,
//         box-shadow 0.18s ease;
// }

// .cayep-classroom-resource-card:hover {
//     border-color: #C9D7E1;
//     box-shadow: 0 5px 18px rgba(20, 37, 54, 0.05);
// }

// .cayep-classroom-resource-icon {
//     width: 45px;
//     height: 45px;
//     flex: 0 0 45px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     border-radius: 12px;
//     background: #F4F7F9;
//     color: #063A6B;
//     font-size: 20px;
// }

// .cayep-classroom-resource-info {
//     min-width: 0;
//     flex: 1;
// }

// .cayep-classroom-resource-top {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     min-width: 0;
// }

// .cayep-classroom-resource-top h4 {
//     min-width: 0;
//     margin: 0;
//     color: #142536;
//     font-size: 13px;
//     font-weight: 800;
//     line-height: 1.4;
//     overflow-wrap: anywhere;
// }

// .cayep-classroom-resource-extension {
//     flex: 0 0 auto;
//     padding: 3px 6px;
//     border-radius: 5px;
//     background: #F4F7F9;
//     color: #6B7280;
//     font-size: 8px;
//     font-weight: 800;
// }

// .cayep-classroom-resource-filename {
//     margin: 4px 0 0;
//     color: #71808D;
//     font-size: 10px;
//     line-height: 1.5;
//     overflow-wrap: anywhere;
// }

// .cayep-classroom-resource-description {
//     margin: 6px 0 0;
//     color: #65737F;
//     font-size: 11px;
//     line-height: 1.65;
// }

// .cayep-classroom-resource-meta {
//     display: flex;
//     align-items: center;
//     flex-wrap: wrap;
//     gap: 5px;
//     margin-top: 7px;
//     color: #8994A0;
//     font-size: 9px;
//     font-weight: 600;
// }

// .cayep-resource-meta-dot {
//     color: #C4C7C7;
// }

// .cayep-classroom-resource-download {
//     flex: 0 0 auto;
//     display: inline-flex;
//     align-items: center;
//     justify-content: center;
//     gap: 7px;
//     min-height: 38px;
//     padding: 9px 13px;
//     border: 0;
//     border-radius: 9px;
//     background: #062A4B;
//     color: #FFFFFF;
//     cursor: pointer;
//     font-family: inherit;
//     font-size: 10px;
//     font-weight: 800;
//     transition:
//         background 0.18s ease,
//         opacity 0.18s ease;
// }

// .cayep-classroom-resource-download:hover:not(:disabled) {
//     background: #063A6B;
// }

// .cayep-classroom-resource-download:disabled {
//     opacity: 0.55;
//     cursor: not-allowed;
// }

// .cayep-classroom-empty {
//     width: 100%;
//     min-height: 280px;
//     display: flex;
//     flex-direction: column;
//     align-items: center;
//     justify-content: center;
//     padding: 35px 20px;
//     text-align: center;
//     color: #142536;
//     border: 1px dashed #C4C7C7;
//     border-radius: 18px;
//     background: #FBFCFD;
// }

// .cayep-classroom-empty-icon {
//     width: 58px;
//     height: 58px;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     margin-bottom: 13px;
//     border-radius: 16px;
//     background: #062A4B;
//     color: #F6C944;
//     font-size: 23px;
// }

// .cayep-classroom-empty h3 {
//     margin: 0 0 7px;
//     font-size: 17px;
//     font-weight: 800;
// }

// .cayep-classroom-empty p {
//     max-width: 430px;
//     margin: 0;
//     color: #6B7280;
//     font-size: 13px;
//     line-height: 1.7;
// }

// @media (max-width: 900px) {
//     .cayep-classroom-header {
//         align-items: flex-start;
//         flex-direction: column;
//         padding: 21px;
//     }

//     .cayep-classroom-status {
//         align-self: flex-start;
//     }

//     .cayep-classroom-content-section {
//         padding: 23px;
//     }

//     .cayep-classroom-tabs button {
//         flex: 0 0 auto;
//     }
// }

// @media (max-width: 700px) {
//     .cayep-classroom-header {
//         border-radius: 14px;
//         padding: 17px;
//     }

//     .cayep-classroom-header-main {
//         width: 100%;
//         gap: 11px;
//     }

//     .cayep-classroom-header-icon {
//         width: 45px;
//         height: 45px;
//         flex-basis: 45px;
//         border-radius: 12px;
//         font-size: 19px;
//     }

//     .cayep-classroom-header-copy h2 {
//         font-size: 18px;
//     }

//     .cayep-classroom-video-context {
//         font-size: 11px;
//     }

//     .cayep-classroom-status {
//         width: 100%;
//         justify-content: center;
//     }

//     .cayep-classroom-tabs {
//         border-radius: 12px;
//     }

//     .cayep-classroom-tabs button {
//         min-height: 42px;
//         padding: 9px 11px;
//         font-size: 10px;
//     }

//     .cayep-classroom-tab-icon {
//         font-size: 12px;
//     }

//     .cayep-classroom-panel {
//         border-radius: 14px;
//     }

//     .cayep-classroom-content-section {
//         padding: 18px 15px;
//     }

//     .cayep-classroom-section-heading {
//         gap: 10px;
//         margin-bottom: 18px;
//     }

//     .cayep-classroom-heading-icon {
//         width: 40px;
//         height: 40px;
//         flex-basis: 40px;
//         border-radius: 11px;
//         font-size: 15px;
//     }

//     .cayep-classroom-section-heading h3 {
//         font-size: 19px;
//     }

//     .cayep-classroom-section-heading p {
//         font-size: 12px;
//     }

//     .cayep-classroom-community-banner,
//     .cayep-classroom-question-form {
//         padding: 13px;
//     }

//     .cayep-classroom-question-form {
//         gap: 9px;
//     }

//     .cayep-classroom-form-avatar {
//         width: 35px;
//         height: 35px;
//         flex-basis: 35px;
//     }

//     .cayep-community-question-row {
//         padding: 13px;
//         gap: 9px;
//     }

//     .cayep-community-avatar {
//         width: 35px;
//         height: 35px;
//     }

//     .cayep-community-author-line {
//         gap: 5px;
//     }

//     .cayep-community-author-line strong {
//         font-size: 11px;
//     }

//     .cayep-community-question-bubble {
//         padding: 11px 12px 11px 20px;
//     }

//     .cayep-community-question-bubble p,
//     .cayep-community-reply-bubble p {
//         font-size: 11px;
//         line-height: 1.65;
//     }

//     .cayep-community-conversation {
//         padding: 0 13px 13px 48px;
//     }

//     .cayep-community-reply-form {
//         padding: 9px;
//     }

//     .cayep-community-reply-form-avatar {
//         width: 30px;
//         height: 30px;
//         flex-basis: 30px;
//     }

//     .cayep-classroom-form-footer,
//     .cayep-community-reply-form-footer,
//     .cayep-classroom-notes-footer {
//         align-items: flex-start;
//         flex-direction: column;
//     }

//     .cayep-classroom-form-footer button,
//     .cayep-community-reply-form-footer button,
//     .cayep-classroom-notes-footer button {
//         width: 100%;
//     }

//     .cayep-classroom-resource-card {
//         align-items: flex-start;
//         flex-wrap: wrap;
//         padding: 12px;
//     }

//     .cayep-classroom-resource-icon {
//         width: 39px;
//         height: 39px;
//         flex-basis: 39px;
//         font-size: 17px;
//     }

//     .cayep-classroom-resource-info {
//         flex: 1 1 calc(100% - 54px);
//     }

//     .cayep-classroom-resource-download {
//         width: 100%;
//         margin-top: 2px;
//     }

//     .cayep-classroom-resource-download span {
//         display: inline;
//     }

//     .cayep-classroom-announcement-card {
//         padding: 13px;
//     }

//     .cayep-classroom-announcement-icon {
//         width: 37px;
//         height: 37px;
//         flex-basis: 37px;
//     }

//     .cayep-classroom-rich-content {
//         padding: 16px;
//     }

//     .cayep-classroom-rich-content p {
//         font-size: 12px;
//     }
// }

// @media (max-width: 430px) {
//     .cayep-classroom-environment {
//         width: 100%;
//     }

//     .cayep-classroom-header {
//         padding: 14px;
//         margin-bottom: 10px;
//     }

//     .cayep-classroom-header-main {
//         align-items: flex-start;
//     }

//     .cayep-classroom-header-copy h2 {
//         font-size: 16px;
//     }

//     .cayep-classroom-eyebrow {
//         font-size: 8px;
//     }

//     .cayep-classroom-video-context {
//         align-items: flex-start;
//         font-size: 10px;
//     }

//     .cayep-classroom-tabs {
//         margin-bottom: 10px;
//         padding: 3px;
//     }

//     .cayep-classroom-tabs button {
//         min-height: 39px;
//         padding: 8px 9px;
//         gap: 5px;
//         font-size: 9px;
//     }

//     .cayep-classroom-tab-label {
//         max-width: 80px;
//         overflow: hidden;
//         text-overflow: ellipsis;
//     }

//     .cayep-classroom-tab-count {
//         min-width: 18px;
//         height: 18px;
//         padding: 0 4px;
//         font-size: 8px;
//     }

//     .cayep-classroom-content-section {
//         padding: 15px 11px;
//     }

//     .cayep-classroom-section-heading {
//         margin-bottom: 15px;
//     }

//     .cayep-classroom-section-heading h3 {
//         font-size: 17px;
//     }

//     .cayep-classroom-section-heading p {
//         font-size: 11px;
//     }

//     .cayep-classroom-context-pill {
//         max-width: 100%;
//         font-size: 9px;
//     }

//     .cayep-classroom-community-banner {
//         gap: 9px;
//     }

//     .cayep-classroom-community-icon {
//         width: 34px;
//         height: 34px;
//         flex-basis: 34px;
//     }

//     .cayep-classroom-community-banner p {
//         font-size: 10px;
//     }

//     .cayep-classroom-question-form {
//         display: block;
//     }

//     .cayep-classroom-form-avatar {
//         margin-bottom: 9px;
//     }

//     .cayep-community-question-row {
//         display: block;
//     }

//     .cayep-community-avatar {
//         margin-bottom: 8px;
//     }

//     .cayep-community-conversation {
//         padding-left: 13px;
//     }

//     .cayep-community-reply-row {
//         gap: 7px;
//     }

//     .cayep-community-reply-avatar {
//         width: 29px;
//         height: 29px;
//     }

//     .cayep-community-role-badge {
//         font-size: 8px;
//     }

//     .cayep-classroom-notes-form textarea {
//         min-height: 220px;
//     }

//     .cayep-classroom-announcement-card {
//         display: block;
//     }

//     .cayep-classroom-announcement-icon {
//         margin-bottom: 10px;
//     }

//     .cayep-classroom-review-card {
//         padding: 14px;
//     }

//     .cayep-classroom-resource-top {
//         align-items: flex-start;
//         flex-direction: column;
//         gap: 5px;
//     }

//     .cayep-classroom-resource-extension {
//         align-self: flex-start;
//     }
// }

// @media (prefers-reduced-motion: reduce) {
//     .cayep-classroom-spinner {
//         animation: none;
//     }

//     .cayep-classroom-tabs button,
//     .cayep-classroom-form-footer button,
//     .cayep-community-reply-form-footer button,
//     .cayep-classroom-notes-footer button,
//     .cayep-classroom-resource-download {
//         transition: none;
//     }
// }
// `;

// export default ClassRoomEnvironment;